/**
 * Chart Renderer for PDF Export
 *
 * Draws horizontal bar charts for all Market Data report types:
 * - Projected Patients (single bar by location/care setting)
 * - Shared Affiliation (stacked bar: affiliated vs non-affiliated)
 * - Shared Patients (single bar per provider, with directional percentage)
 * - Payors (stacked bar: up to 3 payors + other)
 */

import { SpecialtyUtil } from 'doctivity-shared/utils';
import {
  getLocationTypeColor,
  LOCATION_TYPES_FOR_LEGEND,
  PDF_SETTINGS,
  getSettingsByType,
  getColorsByType,
  getLegendByType,
  PAGE_HEADER_STYLES,
  PAGE_FOOTER_STYLES,
} from './pdfStyles';
import { abbreviateOrgName, abbreviateSpecialty } from './abbreviations';

// Colors for Shared Affiliation chart
export const AFFILIATION_COLORS = {
  affiliated: [32, 131, 244],      // Blue - matches CSS rgba(32, 131, 244, 0.7)
  nonAffiliated: [255, 107, 107],  // Red - rgb(255, 107, 107)
};

// Default payor colors matching the on-screen chart (ColorUtil.COLORS)
export const DEFAULT_PAYOR_COLORS = [
  [30, 136, 229],   // Blue - Medicare
  [67, 160, 71],    // Green - Medicaid  
  [255, 167, 38],   // Orange - third payor
  [227, 227, 227],  // Gray (Other)
];

/**
 * Smart title case that preserves acronyms and handles edge cases
 * - Preserves known acronyms (LLC, PC, MD, etc.)
 * - Handles apostrophes correctly (O'Brien, Children's)
 * - Handles hyphenated words
 */
const PRESERVE_UPPERCASE = ['LLC', 'PC', 'MD', 'DO', 'PA', 'NP', 'RN', 'LPN', 'DDS', 'DMD', 'OD', 'DC', 'DPM', 'PhD', 'PharmD', 'PT', 'OT', 'PLLC', 'LP', 'LLP', 'INC', 'CORP', 'CO', 'USA', 'US', 'II', 'III', 'IV', 'VI', 'VII', 'VIII', 'IX', 'XI', 'ASC', 'OR', 'ER', 'ICU', 'NICU', 'CCU', 'ENT', 'OB', 'GYN', 'OBGYN', 'GI', 'CT', 'MRI', 'PET', 'EKG', 'ECG', 'EEG', 'EMG', 'DNA', 'RNA', 'HIV', 'AIDS', 'COPD', 'CHF', 'CAD', 'CABG', 'TIA', 'CVA', 'DVT', 'PE', 'UTI', 'URI', 'GERD', 'IBS', 'ADHD', 'OCD', 'PTSD', 'BP', 'HR', 'BMI'];

const toTitleCase = (str) => {
  if (!str) return str;
  
  // Split on spaces and hyphens, preserving the delimiters
  const words = str.split(/(\s+|-)/);
  
  return words.map(word => {
    // Skip empty strings and delimiters
    if (!word || /^[\s-]+$/.test(word)) return word;
    
    // Check if the word (stripped of punctuation) should be preserved as uppercase
    const stripped = word.replace(/[^a-zA-Z]/g, '').toUpperCase();
    if (PRESERVE_UPPERCASE.includes(stripped)) {
      // Return the word with the acronym uppercased but keep surrounding punctuation
      return word.replace(/[a-zA-Z]+/, stripped);
    }
    
    // Handle apostrophes within words (O'Brien, Children's)
    if (word.includes("'")) {
      return word.split("'").map((part, i) => {
        if (!part) return part;
        // For parts after apostrophe that are just 's' or 't', keep lowercase
        if (i > 0 && (part.toLowerCase() === 's' || part.toLowerCase() === 't')) {
          return part.toLowerCase();
        }
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }).join("'");
    }
    
    // Standard title case for regular words
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join('');
};

/**
 * Convert date range from "Oct 2024 - Sep 2025" to "10/1/24 - 9/30/25" format
 */
const formatDateRangeShort = (dateRange) => {
  if (!dateRange) return '';
  
  const months = {
    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
    'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
  };
  
  // Parse "Mon YYYY - Mon YYYY" format
  const parts = dateRange.split(' - ');
  if (parts.length !== 2) return dateRange;
  
  const formatDate = (dateStr, isEnd) => {
    const [month, year] = dateStr.trim().split(' ');
    const monthNum = months[month];
    if (!monthNum || !year) return dateStr;
    
    // For end date, use last day of month; for start date, use 1st
    const day = isEnd ? new Date(parseInt(year), monthNum, 0).getDate() : 1;
    const shortYear = year.slice(-2);
    return `${monthNum}/${day}/${shortYear}`;
  };
  
  return `${formatDate(parts[0], false)} - ${formatDate(parts[1], true)}`;
};

/**
 * Draws full-page gradient background with angled white section for logo
 * TOP = #18b095 (Teal), BOTTOM = #348cf5 (Blue)
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {string} title - The main title text
 * @param {string} subtitle - Optional subtitle text (empty string for none)
 * @param {Array} filterPills - (not used yet)
 * @param {Object} options - Options including pdfType
 * @returns {number} - Y position for content
 */
export const drawPageHeader = (doc, title, subtitle = '', filterPills = [], options = {}) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const { dateRange } = options;
  
  // Create gradient using canvas for smooth result
  const canvas = document.createElement('canvas');
  canvas.width = 1;  // Only need 1 pixel wide for vertical gradient
  canvas.height = Math.ceil(pageHeight);
  
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#18b095');  // Teal at TOP
  gradient.addColorStop(1, '#348cf5');  // Blue at BOTTOM
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Convert to base64 and add to PDF
  const imgData = canvas.toDataURL('image/png');
  doc.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
  
  // Draw white angled section across top for logo area
  // Quadrilateral: all white from top down to the angled line
  // Line goes from 10px down on left to angleEndY on right
  const { pdfType = 'printable' } = options;
  const anglePercent = pdfType === 'slideshow' ? 0.083 : 0.05;  // 8.3% for slideshow, 5% for printable
  const angleEndY = pageHeight * anglePercent;
  const angleStartY = 10;  // 10px down on the left
  
  doc.setFillColor(255, 255, 255);
  
  // Draw quadrilateral (trapezoid) - everything above the angled line is white
  // Use lines() to draw 4-sided shape
  const points = [
    [pageWidth, 0],                    // Move to top-right
    [0, angleEndY],                    // Down to right side point
    [-pageWidth, angleStartY - angleEndY],  // Across to left side point (0, 10)
    [0, -angleStartY],                 // Up to top-left corner
  ];
  doc.lines(points, 0, 0, [1, 1], 'F', true);
  
  // Add title in the top gradient area - WHITE text
  // Auto-scale font size to fit within 80% of page width
  const maxTitleWidth = pageWidth * 0.80;
  const maxTitleFontSize = 20;
  let titleFontSize = maxTitleFontSize;
  
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(titleFontSize);
  let titleWidth = doc.getTextWidth(title);
  
  // Scale down font size if title is too wide (minimum 12pt)
  while (titleWidth > maxTitleWidth && titleFontSize > 12) {
    titleFontSize -= 0.5;
    doc.setFontSize(titleFontSize);
    titleWidth = doc.getTextWidth(title);
  }
  
  doc.setTextColor(255, 255, 255);  // WHITE
  doc.text(title, 20, 50);  // 20px left (aligned with table), 50px top
  
  // Add optional subtitle if provided
  // Subtitle font size maintains same difference from title (default: 20 - 14 = 6pt smaller)
  if (subtitle) {
    const subtitleFontSize = Math.max(titleFontSize - 6, 8);  // Min 8pt
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(subtitleFontSize);
    doc.setTextColor(255, 255, 255);  // WHITE
    doc.text(subtitle, 20, 68);  // Aligned with title
  }
  
  // Draw white content container (with rounded corners)
  const hasSubtitle = subtitle && subtitle.trim().length > 0;
  const containerTop = hasSubtitle ? 78 : 62;  // Reduced from 80/65
  const containerMargin = {
    top: containerTop,
    bottom: 25,
    left: 10,
    right: 10,
  };
  const containerX = containerMargin.left;
  const containerY = containerTop;
  const containerWidth = pageWidth - containerMargin.left - containerMargin.right;
  const containerHeight = pageHeight - containerMargin.top - containerMargin.bottom;
  const cornerRadius = 8;
  
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(containerX, containerY, containerWidth, containerHeight, cornerRadius, cornerRadius, 'F');
  
  // Add footer text OUTSIDE container (on gradient background)
  const footerY = pageHeight - (containerMargin.bottom / 2) + 3;
  const footerLeftX = containerMargin.left + 5;
  const footerRightX = pageWidth - containerMargin.right - 5;
  
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(220, 230, 255);  // Light/subtle white
  
  // Left aligned: date range + copyright
  const formattedDateRange = formatDateRangeShort(dateRange);
  const dateText = formattedDateRange ? `Data ${formattedDateRange}  ` : '';
  doc.text(`${dateText}© Doctivity Health Proprietary & Confidential Do not Share or Reproduce`, footerLeftX, footerY);
  
  // Right aligned: website
  doc.text('www.DoctivityHealth.com', footerRightX, footerY, { align: 'right' });
  
  // Return container bounds for content positioning
  return {
    y: containerY + 10,  // Y position inside container for content (reduced from +15)
    maxY: pageHeight - containerMargin.bottom - 20,  // Maximum Y before footer
  };
};

/**
 * Draws a continuation page (pages 2+) without title/subtitle
 * Container starts higher since there's no title area needed
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Object} options - Options including pdfType
 * @returns {number} - Y position for content
 */
export const drawContinuationPage = (doc, options = {}) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const { dateRange } = options;
  
  // Create gradient using canvas for smooth result
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = Math.ceil(pageHeight);
  
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#18b095');  // Teal at TOP
  gradient.addColorStop(1, '#348cf5');  // Blue at BOTTOM
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  const imgData = canvas.toDataURL('image/png');
  doc.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
  
  // Draw white angled section across top for logo area
  const { pdfType = 'printable' } = options;
  const anglePercent = pdfType === 'slideshow' ? 0.083 : 0.05;
  const angleEndY = pageHeight * anglePercent;
  const angleStartY = 10;
  
  doc.setFillColor(255, 255, 255);
  
  const points = [
    [pageWidth, 0],
    [0, angleEndY],
    [-pageWidth, angleStartY - angleEndY],
    [0, -angleStartY],
  ];
  doc.lines(points, 0, 0, [1, 1], 'F', true);
  
  // Container starts at 40px on continuation pages (no title/subtitle)
  const containerTop = 40;
  const containerMargin = {
    top: containerTop,
    bottom: 30,
    left: 10,
    right: 10,
  };
  const containerX = containerMargin.left;
  const containerY = containerTop;
  const containerWidth = pageWidth - containerMargin.left - containerMargin.right;
  const containerHeight = pageHeight - containerMargin.top - containerMargin.bottom;
  const cornerRadius = 8;
  
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(containerX, containerY, containerWidth, containerHeight, cornerRadius, cornerRadius, 'F');
  
  // Add footer text
  const footerY = pageHeight - (containerMargin.bottom / 2) + 4;
  const footerLeftX = containerMargin.left + 5;
  const footerRightX = pageWidth - containerMargin.right - 5;
  
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  
  // Left aligned: date range + copyright
  const formattedDateRange = formatDateRangeShort(dateRange);
  const dateText = formattedDateRange ? `Data ${formattedDateRange}  ` : '';
  doc.text(`${dateText}© Doctivity Health Proprietary & Confidential Do not Share or Reproduce`, footerLeftX, footerY);
  doc.text('www.DoctivityHealth.com', footerRightX, footerY, { align: 'right' });
  
  return containerY + 15;  // Return Y position inside container for content
};

/**
 * Draws the page footer with legend and confidentiality notice
 * Matches the Doctivity mockup design
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} legendItems - Array of {name, color, isAffiliatedIndicator} objects for legend
 * @param {Object} options - Options including pdfType
 */
export const drawPageFooterWithLegend = (doc, legendItems = [], options = {}) => {
  const { pdfType = 'printable' } = options;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const styles = PAGE_FOOTER_STYLES[pdfType] || PAGE_FOOTER_STYLES.printable;
  const settings = getSettingsByType(pdfType);
  
  const footerY = pageHeight - styles.height;
  
  // Draw light gray footer background
  doc.setFillColor(...PAGE_FOOTER_STYLES.bgColor);
  doc.rect(0, footerY, pageWidth, styles.height, 'F');
  
  // Draw top border line
  doc.setDrawColor(...PAGE_FOOTER_STYLES.borderColor);
  doc.setLineWidth(0.5);
  doc.line(0, footerY, pageWidth, footerY);
  
  // Draw legend if items provided
  if (legendItems.length > 0) {
    let legendX = settings.margin.left;
    const legendY = footerY + styles.legendY;
    const boxSize = pdfType === 'slideshow' ? 10 : 8;
    
    doc.setFontSize(styles.fontSize);
    
    legendItems.forEach((item, index) => {
      if (item.isAffiliatedIndicator) {
        // Draw the hub icon indicator (small circle with spokes)
        const iconSize = boxSize * 0.8;
        const cx = legendX + iconSize / 2 + 2;
        const cy = legendY + boxSize / 2;
        
        // Draw center circle
        doc.setFillColor(32, 131, 244); // Blue
        doc.circle(cx, cy, iconSize / 3, 'F');
        
        // Draw small spokes
        doc.setDrawColor(32, 131, 244);
        doc.setLineWidth(0.8);
        for (let i = 0; i < 4; i++) {
          const angle = (i * Math.PI) / 2;
          const innerR = iconSize / 3;
          const outerR = iconSize / 2;
          doc.line(
            cx + Math.cos(angle) * innerR,
            cy + Math.sin(angle) * innerR,
            cx + Math.cos(angle) * outerR,
            cy + Math.sin(angle) * outerR
          );
        }
        
        // Label
        doc.setTextColor(60, 60, 60);
        doc.setFont('Roboto', 'normal');
        doc.text('Affiliated', legendX + iconSize + 8, legendY + boxSize - 1);
        legendX += iconSize + 8 + doc.getTextWidth('Affiliated') + 20;
      } else {
        // Draw color box
        doc.setFillColor(...item.color);
        doc.rect(legendX, legendY, boxSize, boxSize, 'F');
        
        // Draw label
        doc.setTextColor(60, 60, 60);
        doc.setFont('Roboto', 'normal');
        doc.text(item.name, legendX + boxSize + 4, legendY + boxSize - 1);
        
        legendX += boxSize + 4 + doc.getTextWidth(item.name) + 20;
      }
    });
  }
  
  // Draw confidentiality notice
  doc.setFontSize(styles.confidentialFontSize);
  doc.setTextColor(...PAGE_FOOTER_STYLES.textColor);
  doc.setFont('Roboto', 'normal');
  doc.text(PAGE_FOOTER_STYLES.confidentialText, settings.margin.left, footerY + styles.confidentialY);
};

/**
 * Draws a generic legend at the top of the chart
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} items - Array of {name, color} objects
 * @param {number} x - Starting X position
 * @param {number} y - Starting Y position
 * @param {number} maxWidth - Maximum width available
 * @param {string} pdfType - 'slideshow' or 'printable'
 * @returns {number} - New Y position after legend
 */
export const drawChartLegend = (doc, items, x, y, maxWidth, pdfType = 'printable') => {
  const settings = getSettingsByType(pdfType);
  const boxSize = pdfType === 'slideshow' ? 10 : 8;
  const gap = pdfType === 'slideshow' ? 6 : 6;
  let currentX = x;
  let currentY = y;
  
  doc.setFontSize(settings.chart.fontSize.legend);
  
  items.forEach((item, idx) => {
    const labelWidth = doc.getTextWidth(item.name);
    const itemWidth = boxSize + 4 + labelWidth + gap;
    
    // Wrap to next line if needed
    if (currentX + itemWidth > x + maxWidth && idx > 0) {
      currentX = x;
      currentY += pdfType === 'slideshow' ? 14 : 12;
    }
    
    // Draw color box
    doc.setFillColor(...item.color);
    doc.rect(currentX, currentY, boxSize, boxSize, 'F');
    
    // Draw label
    doc.setTextColor(60, 60, 60);
    doc.setFont('Roboto', 'normal');
    doc.text(item.name, currentX + boxSize + 4, currentY + (pdfType === 'slideshow' ? 8 : 6));
    
    currentX += itemWidth + (pdfType === 'slideshow' ? 10 : 8);
  });
  
  return currentY + (pdfType === 'slideshow' ? 16 : 14);
};

/**
 * Draw location type legend (for Projected Patients)
 */
export const drawLocationTypeLegend = (doc, x, y, maxWidth, pdfType = 'printable') => {
  const legendItems = getLegendByType(pdfType);
  return drawChartLegend(doc, legendItems, x, y, maxWidth, pdfType);
};

/**
 * Draw affiliation legend (for Shared Affiliation)
 */
export const drawAffiliationLegend = (doc, x, y, maxWidth, pdfType = 'printable') => {
  const items = [
    { name: 'Affiliated', color: AFFILIATION_COLORS.affiliated },
    { name: 'Non-Affiliated', color: AFFILIATION_COLORS.nonAffiliated },
  ];
  return drawChartLegend(doc, items, x, y, maxWidth, pdfType);
};

/**
 * Draw payor legend (for Payors chart)
 */
export const drawPayorLegend = (doc, payors, x, y, maxWidth, pdfType = 'printable') => {
  const items = payors.map((payor, idx) => ({
    name: payor.name,
    color: payor.color || DEFAULT_PAYOR_COLORS[idx] || DEFAULT_PAYOR_COLORS[3],
  }));
  // Add "Other" 
  items.push({ name: 'Other Payors', color: DEFAULT_PAYOR_COLORS[3] });
  return drawChartLegend(doc, items, x, y, maxWidth, pdfType);
};

/**
 * Draws an affiliated provider icon (hub icon representation)
 * Since we can't use Material Icons in PDF, we draw a simple indicator
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {number} x - X position
 * @param {number} y - Y position
 * @param {Array} color - RGB color array
 * @param {string} pdfType - 'slideshow' or 'printable'
 */
const drawAffiliatedIndicator = (doc, x, y, color, pdfType = 'printable') => {
  const scale = pdfType === 'slideshow' ? 1 : 0.7;
  const circleRadius = 3 * scale;
  const outerRadius = 5 * scale;
  const offset = 4 * scale;
  
  // Draw a small filled circle as the hub indicator
  doc.setFillColor(...color);
  doc.circle(x + offset, y + offset, circleRadius, 'F');
  
  // Draw small lines radiating out (simplified hub icon)
  doc.setDrawColor(...color);
  doc.setLineWidth(0.5 * scale);
  const cx = x + offset;
  const cy = y + offset;
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    doc.line(
      cx + Math.cos(angle) * circleRadius,
      cy + Math.sin(angle) * circleRadius,
      cx + Math.cos(angle) * outerRadius,
      cy + Math.sin(angle) * outerRadius
    );
  }
};

/**
 * Check if we need to add a new page
 */
const checkPageBreak = (doc, currentY, barHeight, barGap, options = {}) => {
  const pdfType = options.pdfType || 'printable';
  const settings = getSettingsByType(pdfType);
  
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  const footerHeight = settings.chart.footerHeight;
  const legendHeight = options.showLegend ? settings.chart.legendHeight : 0;
  const bottomMargin = footerHeight + legendHeight + settings.margin.bottom;
  
  if (currentY + barHeight + barGap > pageHeight - bottomMargin) {
    // Draw legend at bottom of current page before adding new page
    if (options.showLegend && options.drawLegend) {
      const effectiveWidth = pageWidth - settings.margin.left - settings.margin.right;
      const legendY = pageHeight - footerHeight - legendHeight;
      options.drawLegend(doc, settings.margin.left, legendY, effectiveWidth);
    }
    
    doc.addPage();
    return settings.margin.top;
  }
  return currentY;
};

/**
 * Truncate text to fit within a given pixel width using actual font metrics
 */
const truncateText = (doc, text, maxWidth) => {
  if (!text) return '';
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 1 && doc.getTextWidth(truncated + '…') > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + '…';
};

/**
 * Draws the Projected Patients horizontal bar chart
 * Matches the ProviderProjectedPatientsChart React component
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Array} data - Array of claim rows from CLAIMS_PER_PROVIDER
 * @param {Object} options - Chart rendering options
 * @returns {number} - New Y position after chart
 */
export const drawProjectedPatientsChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent column widths across pages
    showLegend = true,
    patientType = 'ALL_PATIENTS',
    claimsType = 'providers',
    pdfType = 'printable',
    filters = {},  // Pass filters to check for single specialty
    selectedColumns = null,  // Optional: column visibility selection from PDFExportDialog
  } = options;

  // Get settings based on pdfType
  const settings = getSettingsByType(pdfType);
  
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Table dimensions
  const startX = x || 20;  // Left margin inside container
  const tableWidth = width || pageWidth - 40;  // Full width minus margins
  
  // Row settings - reduced to fit more content
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  
  // Colors - Blue for affiliated, Red for non-affiliated
  const affiliatedBarColor = [83, 145, 213];  // Blue
  const nonAffiliatedBarColor = [255, 107, 107];  // Red - rgb(255, 107, 107)
  const headerBgColor = [235, 241, 248];  // Light blue-gray
  const headerTextColor = [100, 116, 139];  // Gray
  const rowBgColor = [255, 255, 255];  // White
  // Alternating row: header color at ~40% opacity (blended with white)
  const altRowBgColor = [243, 247, 251];  // Lighter version of header color
  
  let currentY = y;
  
  // Check if this is captured claims
  const isCapturedClaims = patientType === 'CAPTURED_CLAIMS' || patientType === 'PEDIATRIC_PATIENTS';
  
  // Column visibility - use selectedColumns if provided, otherwise use defaults
  // For columns that may not exist in all report types, check for explicit true
  const showIcon = selectedColumns ? selectedColumns.icon !== false : true;
  const showName = selectedColumns ? selectedColumns.name !== false : true;
  const showCredential = selectedColumns ? selectedColumns.credential === true : false;  // Default off
  const showSpecialtyCol = selectedColumns ? (selectedColumns.specialty !== false && selectedColumns.facilityType !== false) : true;
  const showOrgCol = selectedColumns ? selectedColumns.organization !== false : true;
  const showSOCCol = selectedColumns ? selectedColumns.soc === true : true;  // Only show if explicitly true (not present in captured_claims)
  const showPatientsCol = selectedColumns ? selectedColumns.patients !== false : true;
  const showMarketPct = selectedColumns ? selectedColumns.marketPct !== false : true;
  const showChart = selectedColumns ? selectedColumns.chart !== false : true;
  
  // Check if single specialty is filtered (hide specialty column if so)
  const hasSingleSpecialty = (
    (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') ||
    (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length === 1)
  );
  
  // Check if care setting is 'All' - show SOC column if so (but respect selectedColumns)
  const careSettingAllowsSOC = !filters.careSetting || filters.careSetting === 'All' || filters.careSetting === '';
  const showSOCColumn = showSOCCol && careSettingAllowsSOC && !isCapturedClaims;  // Never show SOC for captured claims
  
  // SOC abbreviation mapping based on location_type
  const getSOCAbbreviation = (locationType) => {
    switch (locationType) {
      case 1: return 'ASC';
      case 2: return 'Outpt';
      case 3: return 'Office';
      case 4: return 'Inpt';
      case 5: return 'Lab';
      case 6: return 'UC';
      default: return '';
    }
  };
  
  // Find max value for scaling the stacked bars
  // Use allData if provided (for consistent bar scaling across pages), otherwise use data
  const dataForMax = allData || data;
  const maxValue = dataForMax.reduce((max, row) => {
    const total = parseInt(row.total, 10) || 0;
    return Math.max(max, total);
  }, 100);

  // === Measure actual data to auto-size name column ===
  // For organizations: Organization Name, Specialty (no separate org column)
  // For providers: Provider Name, Specialty (optional), Organization
  const isOrganizations = claimsType === 'organizations';
  // Specialty column: show if not single specialty AND user hasn't hidden it
  const showSpecialtyColumn = showSpecialtyCol && (!hasSingleSpecialty || isOrganizations);
  // Organization column: only for providers, respect selectedColumns
  const showOrganizationColumn = !isOrganizations && showOrgCol;
  
  // Measure the widest name text
  doc.setFontSize(fontSize);
  const rowsToMeasure = (allData || data).slice(0, maxRows);

  let maxNameW = 0;
  rowsToMeasure.forEach((rowData) => {
    let name;
    if (isOrganizations) {
      name = rowData.organization?.name || rowData.name || 'Unknown Organization';
    } else {
      const fn = rowData.provider?.first_name || '';
      const ln = rowData.provider?.last_name || '';
      name = `${ln}, ${fn}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
    }
    doc.setFont('Roboto', 'bold');
    maxNameW = Math.max(maxNameW, doc.getTextWidth(name));
  });

  // Add padding to measured width to account for rendering offsets
  // Rendering uses +5 for left offset and -10 for truncation width
  const cellPadding = 30;
  maxNameW += cellPadding;

  // Column widths
  const iconW = showIcon ? 0.03 : 0;
  const credentialW = showCredential && !isOrganizations ? 0.06 : 0;  // Only for providers
  const socW = showSOCColumn ? 0.08 : 0;
  const patientsW = showPatientsCol ? 0.12 : 0;
  const pctNumberW = showMarketPct ? 0.08 : 0;
  
  // Check if any right-side columns are visible
  const hasRightCols = showPatientsCol || showMarketPct || showChart;
  
  // Count visible text columns to distribute space (credential is fixed width, not part of distribution)
  const visibleTextCols = [showName, showSpecialtyColumn, showOrganizationColumn].filter(Boolean).length;
  
  let namePct, specialtyPct, orgPct;
  
  if (!hasRightCols && !showSOCColumn) {
    // No right-side columns or SOC - text columns should expand to fill space
    const availableWidth = 1.0 - iconW;
    if (visibleTextCols === 0) {
      namePct = 0;
      specialtyPct = 0;
      orgPct = 0;
    } else if (visibleTextCols === 1) {
      // Single column gets all the space
      namePct = showName ? availableWidth : 0;
      specialtyPct = showSpecialtyColumn ? availableWidth : 0;
      orgPct = showOrganizationColumn ? availableWidth : 0;
    } else if (visibleTextCols === 2) {
      // Two columns split the space
      if (showName && showSpecialtyColumn) {
        namePct = availableWidth * 0.5;
        specialtyPct = availableWidth * 0.5;
        orgPct = 0;
      } else if (showName && showOrganizationColumn) {
        namePct = availableWidth * 0.45;
        specialtyPct = 0;
        orgPct = availableWidth * 0.55;
      } else {
        // specialty and org only
        namePct = 0;
        specialtyPct = availableWidth * 0.4;
        orgPct = availableWidth * 0.6;
      }
    } else {
      // All three columns
      namePct = availableWidth * 0.35;
      specialtyPct = availableWidth * 0.25;
      orgPct = availableWidth * 0.40;
    }
  } else {
    // Right-side columns or SOC shown - use normal proportions
    if (isOrganizations) {
      specialtyPct = showSpecialtyColumn ? 0.12 : 0;
      orgPct = 0;
    } else {
      if (showSpecialtyColumn && showOrganizationColumn) {
        specialtyPct = 0.12;
        orgPct = 0.18;
      } else if (showSpecialtyColumn) {
        specialtyPct = 0.12;
        orgPct = 0;
      } else if (showOrganizationColumn) {
        specialtyPct = 0;
        orgPct = 0.18;
      } else {
        specialtyPct = 0;
        orgPct = 0;
      }
    }
    
    // Name column: use 40% when specialty/org are hidden, otherwise use measured width (capped at 40%)
    const neededNamePct = maxNameW / tableWidth;
    if (!showSpecialtyColumn && !showOrganizationColumn) {
      // With fewer columns, name takes full 40%
      namePct = 0.40;
    } else {
      // With more columns, use measured width (capped)
      namePct = Math.min(neededNamePct, 0.40);
      namePct = Math.max(namePct, 0.15);
    }
  }

  // Build column layout dynamically based on visibility
  let columns = {};
  let currentStart = 0;
  
  if (showIcon) {
    columns.icon = { start: currentStart, width: iconW };
    currentStart += iconW;
  }
  
  // Name column - only include if checked
  if (showName) {
    columns.name = { start: currentStart, width: namePct };
    currentStart += namePct;
  }
  
  // Credential column - only for providers
  if (showCredential && !isOrganizations) {
    columns.credential = { start: currentStart, width: credentialW };
    currentStart += credentialW;
  }
  
  if (showSpecialtyColumn) {
    columns.specialty = { start: currentStart, width: specialtyPct };
    currentStart += specialtyPct;
  }
  
  if (showOrganizationColumn) {
    columns.organization = { start: currentStart, width: orgPct };
    currentStart += orgPct;
  }
  
  if (showSOCColumn) {
    columns.soc = { start: currentStart, width: socW };
    currentStart += socW;
  }
  
  // Right-side columns - last visible one extends to table edge
  const hasChart = showChart;
  const hasMarketPct = showMarketPct;
  const hasPatients = showPatientsCol;
  
  if (hasPatients) {
    // If this is the last right column, extend to edge
    const isLast = !hasMarketPct && !hasChart;
    const width = isLast ? (1.0 - currentStart) : patientsW;
    columns.patients = { start: currentStart, width: width };
    currentStart += width;
  }
  
  if (hasMarketPct) {
    // If this is the last right column, extend to edge
    const isLast = !hasChart;
    const width = isLast ? (1.0 - currentStart) : pctNumberW;
    columns.pctNumber = { start: currentStart, width: width };
    currentStart += width;
  }
  
  if (hasChart) {
    // Chart column always extends to edge of table
    const chartW = 1.0 - currentStart;
    columns.chart = { start: currentStart, width: chartW };
  }

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    // Draw rounded rect for full header, then cover bottom corners with a flat rect
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    // Cover bottom rounded corners with a flat rect on the bottom half
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    
    // Build headers dynamically based on which columns are visible
    const rightColumnHeader = isCapturedClaims ? 'CLAIMS & MARKET %' : 'PATIENTS & MARKET %';
    let headers = [];
    
    // Name column (always shown, but header changes if hidden)
    if (columns.name) {
      headers.push({ 
        text: isOrganizations ? 'ORGANIZATION' : 'PROVIDER', 
        col: 'name' 
      });
    }
    
    // Credential column (providers only)
    if (columns.credential) {
      headers.push({ text: 'CRED', col: 'credential' });
    }
    
    // Specialty/Facility Type column
    if (showSpecialtyColumn && columns.specialty) {
      headers.push({ 
        text: isOrganizations ? 'FACILITY TYPE' : 'SPECIALTY', 
        col: 'specialty' 
      });
    }
    
    // Organization column (providers only)
    if (showOrganizationColumn && columns.organization) {
      headers.push({ text: 'ORGANIZATION', col: 'organization' });
    }
    
    // SOC column
    if (showSOCColumn && columns.soc) {
      headers.push({ text: 'SOC', col: 'soc' });
    }
    
    // Right-side columns (patients, pct, chart) - combine into one header if multiple visible
    const rightColsVisible = [showPatientsCol, showMarketPct, showChart].filter(Boolean).length;
    if (rightColsVisible > 0) {
      // Find the first visible right column for positioning
      const firstRightCol = showPatientsCol ? 'patients' : (showMarketPct ? 'pctNumber' : 'chart');
      headers.push({ 
        text: rightColumnHeader, 
        col: firstRightCol, 
        spanRight: true  // Special flag to span all right columns
      });
    }
    
    headers.forEach(header => {
      if (!columns[header.col]) return;  // Skip if column doesn't exist
      
      const colX = startX + columns[header.col].start * tableWidth + 5;
      let colWidth;
      if (header.spanRight) {
        // Calculate total width of all visible right columns
        let totalRightWidth = 0;
        if (columns.patients) totalRightWidth += columns.patients.width;
        if (columns.pctNumber) totalRightWidth += columns.pctNumber.width;
        if (columns.chart) totalRightWidth += columns.chart.width;
        colWidth = totalRightWidth * tableWidth - 10;
      } else {
        colWidth = columns[header.col].width * tableWidth - 10;
      }
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw a single row ===
  const drawRow = (rowData, index) => {
    const total = parseInt(rowData.total, 10) || 0;
    const locationType = rowData.location_type || 3;
    
    // Handle both providers and organizations
    let displayName = '';
    let isAffiliated = false;
    let orgName = '';
    let specialty = '';
    
    if (claimsType === 'organizations') {
      const org = rowData.organization || rowData.dataValues?.organization || {};
      const rawName = org.name || org.dataValues?.name || rowData.name || 'Unknown Organization';
      displayName = rawName.toUpperCase();  // ALL CAPS for org names
      isAffiliated = rowData.affiliated === true || 
                    org.is_affiliated === true || 
                    (org.affiliated?.length > 0);
      // Use facility_type for organizations - check nested org first since that's where it comes from
      const rawFacilityType = org.facility_type || org.dataValues?.facility_type ||
                          rowData.facility_type || rowData.dataValues?.facility_type || '';
      specialty = toTitleCase(rawFacilityType);
    } else {
      // Provider data structure
      const firstName = rowData.provider?.first_name || '';
      const lastName = rowData.provider?.last_name || '';
      const rawName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
      displayName = rawName.toUpperCase();  // ALL CAPS for provider names
      
      // Check affiliation - NULL means not affiliated
      isAffiliated = rowData.affiliated === true || 
                    rowData.provider?.is_affiliated === true || 
                    (rowData.provider?.clients?.length > 0);
      
      // Organization from provider.location.organization
      const rawOrg = rowData.provider?.location?.organization?.name ||
                rowData.organization_name ||
                rowData.location?.organization?.name || '';
      orgName = toTitleCase(rawOrg);
      
      // Specialty from taxonomy using SpecialtyUtil
      const taxonomy = rowData.provider?.taxonomy || rowData.taxonomy;
      if (taxonomy) {
        specialty = SpecialtyUtil.getNameFromTaxonomy(taxonomy) || '';
      } else {
        // Fallback to other specialty fields
        specialty = toTitleCase(rowData.provider?.specialty || rowData.specialty || '');
      }
    }
    
    // Get credential for providers
    const credential = !isOrganizations ? (rowData.provider?.credential || rowData.credential || '') : '';
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Icon column (separate column for affiliation icon) ===
    if (showIcon && columns.icon) {
      const iconX = startX + columns.icon.start * tableWidth + 5;
      if (isAffiliated) {
        // Hub icon for affiliated (matching mockup)
        drawAffiliatedHubIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, affiliatedBarColor);
      }
      // Non-affiliated: leave blank (no icon)
    }
    
    // === Name column (Provider name or Organization name) ===
    if (showName && columns.name) {
      const nameX = startX + columns.name.start * tableWidth + 5;
      doc.setFontSize(nameFontSize);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(33, 37, 41);
      const nameColWidth = columns.name.width * tableWidth - 10;
      const nameToDraw = truncateText(doc, displayName, nameColWidth);
      doc.text(nameToDraw, nameX, textY);
    }
    
    // === Credential column (providers only) ===
    if (columns.credential) {
      const credX = startX + columns.credential.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const credColWidth = columns.credential.width * tableWidth - 10;
      const credToDraw = truncateText(doc, credential || '-', credColWidth);
      doc.text(credToDraw, credX, textY);
    }
    
    // === Specialty column (only if shown) ===
    if (showSpecialtyColumn && columns.specialty) {
      const specialtyX = startX + columns.specialty.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const specColWidth = columns.specialty.width * tableWidth - 10;
      const specToDraw = truncateText(doc, specialty || '-', specColWidth);
      doc.text(specToDraw, specialtyX, textY);
    }
    
    // === Organization column (only for providers) ===
    if (showOrganizationColumn && columns.organization) {
      const orgX = startX + columns.organization.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const orgColWidth = columns.organization.width * tableWidth - 10;
      const orgToDraw = truncateText(doc, orgName || '-', orgColWidth);
      doc.text(orgToDraw, orgX, textY);
    }
    
    // === SOC column (only if care setting is 'All') ===
    if (showSOCColumn && columns.soc) {
      const socX = startX + columns.soc.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const socColWidth = columns.soc.width * tableWidth - 10;
      const socAbbrev = getSOCAbbreviation(locationType);
      const socToDraw = truncateText(doc, socAbbrev || '-', socColWidth);
      doc.text(socToDraw, socX, textY);
    }
    
    // === Patients/Claims column ===
    if (showPatientsCol && columns.patients) {
      const patientsX = startX + columns.patients.start * tableWidth + 2;
      
      // Person icon - only for projected patients, not captured claims
      if (!isCapturedClaims) {
        drawPersonIcon(doc, patientsX, currentY + (rowHeight - iconSize) / 2, iconSize, [156, 163, 175]);
      }
      
      // Patient/Claims count
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const countText = total > 0 ? total.toLocaleString() : '<11';
      const countX = isCapturedClaims ? patientsX : patientsX + iconSize + 2;
      doc.text(countText, countX, textY);
    }
    
    // === Percentage number column (next to patients) ===
    if (showMarketPct && columns.pctNumber) {
      const pctNumX = startX + columns.pctNumber.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      
      let pctText = '-';
      if (rowData.aggregateClaimsPercentage !== null && rowData.aggregateClaimsPercentage !== undefined) {
        const pctValue = rowData.aggregateClaimsPercentage * 100;
        pctText = `${pctValue.toFixed(2)}%`;
      }
      doc.text(pctText, pctNumX, textY);
    }
    
    // === Chart column (bar only, no percentage text) ===
    if (showChart && columns.chart) {
      const chartX = startX + columns.chart.start * tableWidth + 15;  // Extra spacing from percentage
      const barWidth = columns.chart.width * tableWidth - 20;  // Reduce width to account for spacing
      const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
      const barY = currentY + (rowHeight - barHeight) / 2;
      
      // Calculate bar width based on total relative to max
      const scale = maxValue > 0 ? (total / maxValue) : 0;
      const scaledBarWidth = barWidth * scale;
      
      // Draw single bar - red if affiliated, blue if non-affiliated
      // Use subtle rounded corners (2px radius)
      const barRadius = 2;
      if (scaledBarWidth > 0) {
        const barColor = isAffiliated ? affiliatedBarColor : nonAffiliatedBarColor;
        doc.setFillColor(...barColor);
        doc.roundedRect(chartX, barY, Math.max(scaledBarWidth, barRadius * 2), barHeight, barRadius, barRadius, 'F');
      }
    }
    
    currentY += rowHeight;
  };

  // === Draw legend (matching mockup: hub icon + "Affiliated", blue box + "Affiliated", red box + "Non-Affiliated") ===
  const drawTableLegend = () => {
    const legendY = currentY + 10;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    const boxSize = pdfType === 'slideshow' ? 14 : 10;
    const legendFontSize = pdfType === 'slideshow' ? 10 : 8;
    let legendX = startX + 10;
    
    console.log('Legend drawing at:', { legendX, legendY, startX, currentY, pdfType });
    
    doc.setFontSize(legendFontSize);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(33, 37, 41);
    
    // 1. Affiliated hub icon + "Affiliated" (red)
    console.log('Drawing hub icon at:', legendX, legendY);
    drawAffiliatedHubIcon(doc, legendX, legendY, iconSize, affiliatedBarColor);
    legendX += iconSize + 4;
    doc.text('Affiliated', legendX, legendY + iconSize / 2 + 3);
    legendX += doc.getTextWidth('Affiliated') + 20;
    
    // 2. Red box + "Affiliated"
    console.log('Drawing red box at:', legendX, legendY);
    doc.setFillColor(...affiliatedBarColor);
    doc.rect(legendX, legendY + 1, boxSize, boxSize - 2, 'F');
    legendX += boxSize + 4;
    doc.text('Affiliated', legendX, legendY + iconSize / 2 + 3);
    legendX += doc.getTextWidth('Affiliated') + 20;
    
    // 3. Blue box + "Non-Affiliated"
    console.log('Drawing blue box at:', legendX, legendY);
    doc.setFillColor(...nonAffiliatedBarColor);
    doc.rect(legendX, legendY + 1, boxSize, boxSize - 2, 'F');
    legendX += boxSize + 4;
    doc.text('Non-Affiliated', legendX, legendY + iconSize / 2 + 3);
    
    console.log('Legend complete');
    currentY = legendY + iconSize + 15;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = data.slice(0, maxRows);
  rowsToRender.forEach((rowData, index) => {
    drawRow(rowData, index);
  });

  return currentY + 10;
};

/**
 * Draw affiliated hub icon (network/connection icon matching Material Icons "hub")
 * Central circle with lines radiating to smaller outer circles
 */
const drawAffiliatedHubIcon = (doc, x, y, size, color) => {
  doc.setFillColor(...color);
  doc.setDrawColor(...color);
  doc.setLineWidth(size * 0.08);
  
  const centerX = x + size / 2;
  const centerY = y + size / 2;
  const centerRadius = size * 0.18;
  const outerRadius = size * 0.10;
  const orbitRadius = size * 0.38;
  
  // Draw lines from center to outer nodes first (so circles draw on top)
  const nodeCount = 5;
  for (let i = 0; i < nodeCount; i++) {
    const angle = (i * 2 * Math.PI / nodeCount) - Math.PI / 2; // Start from top
    const nodeX = centerX + Math.cos(angle) * orbitRadius;
    const nodeY = centerY + Math.sin(angle) * orbitRadius;
    doc.line(centerX, centerY, nodeX, nodeY);
  }
  
  // Draw center circle
  doc.circle(centerX, centerY, centerRadius, 'F');
  
  // Draw outer node circles
  for (let i = 0; i < nodeCount; i++) {
    const angle = (i * 2 * Math.PI / nodeCount) - Math.PI / 2; // Start from top
    const nodeX = centerX + Math.cos(angle) * orbitRadius;
    const nodeY = centerY + Math.sin(angle) * orbitRadius;
    doc.circle(nodeX, nodeY, outerRadius, 'F');
  }
};

/**
 * Draw person/user icon
 */
const drawPersonIcon = (doc, x, y, size, color) => {
  doc.setFillColor(...color);
  
  // Head (circle)
  const headRadius = size * 0.25;
  const headY = y + headRadius + 1;
  doc.circle(x + size / 2, headY, headRadius, 'F');
  
  // Body (arc/shoulders)
  const bodyY = headY + headRadius + 1;
  const bodyWidth = size * 0.7;
  const bodyHeight = size * 0.4;
  doc.ellipse(x + size / 2, bodyY + bodyHeight / 2, bodyWidth / 2, bodyHeight / 2, 'F');
};

/**
 * Draws affiliation summary badges (like "~0% Affiliated" "~100% Non-Affiliated")
 */
export const drawAffiliationSummary = (doc, counts, x, y, options = {}) => {
  if (!counts || !counts.total) return y;
  
  const { pdfType = 'printable' } = options;
  const scale = pdfType === 'slideshow' ? 1 : 0.7;
  
  const affiliatedPct = ((counts.total_affiliated / counts.total) * 100).toFixed(0);
  const nonAffiliatedPct = (100 - affiliatedPct).toFixed(0);
  
  doc.setFontSize(pdfType === 'slideshow' ? 10 : 7);
  doc.setFont('Roboto', 'normal');
  
  const badgeHeight = 22 * scale;
  const badgeWidth1 = 100 * scale;
  const badgeWidth2 = 120 * scale;
  const textPadding = 8 * scale;
  const textY = y + 15 * scale;
  const badgeGap = 10 * scale;
  
  // Affiliated badge
  doc.setFillColor(230, 243, 255);
  doc.rect(x, y, badgeWidth1, badgeHeight, 'F');
  doc.setTextColor(...AFFILIATION_COLORS.affiliated);
  doc.text(`~${affiliatedPct}% Affiliated`, x + textPadding, textY);
  
  // Non-affiliated badge
  doc.setFillColor(255, 235, 238);
  doc.rect(x + badgeWidth1 + badgeGap, y, badgeWidth2, badgeHeight, 'F');
  doc.setTextColor(...AFFILIATION_COLORS.nonAffiliated);
  doc.text(`~${nonAffiliatedPct}% Non-Affiliated`, x + badgeWidth1 + badgeGap + textPadding, textY);
  
  doc.setTextColor(60, 60, 60);
  
  return y + badgeHeight + (pdfType === 'slideshow' ? 10 : 6);
};

/**
 * Draws the Shared Affiliation chart in table format (matching Projected Patients UI)
 */
export const drawSharedAffiliationChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent column widths across pages
    showLegend = true,
    claimsType = 'providers',
    pdfType = 'printable',
    filters = {},
    selectedColumns = null,  // Optional: column visibility selection from PDFExportDialog
  } = options;

  const settings = getSettingsByType(pdfType);
  const pageWidth = doc.internal.pageSize.width;
  
  // Table dimensions
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;
  
  // Row settings
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  
  // Colors - Blue for affiliated, Red for non-affiliated
  const affiliatedBarColor = AFFILIATION_COLORS.affiliated;
  const nonAffiliatedBarColor = AFFILIATION_COLORS.nonAffiliated;
  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];
  
  let currentY = y;
  
  const isOrganizations = claimsType === 'organizations';
  
  // Column visibility - use selectedColumns if provided, otherwise use defaults
  const showIcon = selectedColumns ? selectedColumns.icon !== false : true;
  const showName = selectedColumns ? selectedColumns.name !== false : true;
  const showCredential = selectedColumns ? selectedColumns.credential === true : false;  // Default off
  const showSpecialtyCol = selectedColumns ? (selectedColumns.specialty !== false && selectedColumns.facilityType !== false) : true;
  const showOrgCol = selectedColumns ? selectedColumns.organization !== false : true;
  const showAffiliatedPct = selectedColumns ? selectedColumns.affiliatedPct !== false : true;
  const showChart = selectedColumns ? selectedColumns.chart !== false : true;
  
  // Check if single specialty is filtered (hide specialty column if so)
  const hasSingleSpecialty = (
    (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') ||
    (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length === 1)
  );
  const showSpecialtyColumn = showSpecialtyCol && (!hasSingleSpecialty || isOrganizations);
  const showOrganizationColumn = !isOrganizations && showOrgCol;
  
  // Find max value for scaling bars - use allData if provided
  const dataForMax = allData || data;
  const maxValue = dataForMax.reduce((max, row) => {
    const total = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    return Math.max(max, total);
  }, 100);

  // Column layout for Shared Affiliation (no patient count column)
  // Base widths
  const iconW = showIcon ? 0.03 : 0;
  const credentialW = showCredential && !isOrganizations ? 0.06 : 0;  // Only for providers
  const pctAffiliatedW = showAffiliatedPct ? 0.11 : 0;
  
  // Count visible text columns to distribute space
  const visibleTextCols = [showName, showSpecialtyColumn, showOrganizationColumn].filter(Boolean).length;
  
  let namePct, specialtyPct, orgPct;
  
  if (!showChart && !showAffiliatedPct) {
    // No chart or pct - text columns should expand to fill space
    const availableWidth = 1.0 - iconW;
    if (visibleTextCols === 0) {
      namePct = 0;
      specialtyPct = 0;
      orgPct = 0;
    } else if (visibleTextCols === 1) {
      // Single column gets all the space
      namePct = showName ? availableWidth : 0;
      specialtyPct = showSpecialtyColumn ? availableWidth : 0;
      orgPct = showOrganizationColumn ? availableWidth : 0;
    } else if (visibleTextCols === 2) {
      // Two columns split the space
      if (showName && showSpecialtyColumn) {
        namePct = availableWidth * 0.5;
        specialtyPct = availableWidth * 0.5;
        orgPct = 0;
      } else if (showName && showOrganizationColumn) {
        namePct = availableWidth * 0.45;
        specialtyPct = 0;
        orgPct = availableWidth * 0.55;
      } else {
        // specialty and org only
        namePct = 0;
        specialtyPct = availableWidth * 0.4;
        orgPct = availableWidth * 0.6;
      }
    } else {
      // All three columns
      namePct = availableWidth * 0.35;
      specialtyPct = availableWidth * 0.25;
      orgPct = availableWidth * 0.40;
    }
  } else {
    // Chart or pct is shown - use normal proportions
    if (isOrganizations) {
      namePct = 0.40;
      specialtyPct = showSpecialtyColumn ? 0.12 : 0;
      orgPct = 0;
    } else {
      namePct = showName ? 0.40 : 0;
      if (showSpecialtyColumn && showOrganizationColumn) {
        specialtyPct = 0.12;
        orgPct = 0.20;
      } else if (showSpecialtyColumn) {
        specialtyPct = 0.12;
        orgPct = 0;
      } else if (showOrganizationColumn) {
        specialtyPct = 0;
        orgPct = 0.20;
      } else {
        specialtyPct = 0;
        orgPct = 0;
      }
    }
  }
  
  // Build column layout dynamically based on visibility
  let columns = {};
  let currentStart = 0;
  
  if (showIcon) {
    columns.icon = { start: currentStart, width: iconW };
    currentStart += iconW;
  }
  
  // Name column - only include if checked
  if (showName) {
    columns.name = { start: currentStart, width: namePct };
    currentStart += namePct;
  }
  
  // Credential column - only for providers
  if (showCredential && !isOrganizations) {
    columns.credential = { start: currentStart, width: credentialW };
    currentStart += credentialW;
  }
  
  if (showSpecialtyColumn) {
    columns.specialty = { start: currentStart, width: specialtyPct };
    currentStart += specialtyPct;
  }
  
  if (showOrganizationColumn) {
    columns.organization = { start: currentStart, width: orgPct };
    currentStart += orgPct;
  }
  
  // Right-side columns - last visible one extends to table edge
  const hasChart = showChart;
  const hasAffiliatedPct = showAffiliatedPct;
  
  if (hasAffiliatedPct) {
    // If this is the last right column, extend to edge
    const isLast = !hasChart;
    const width = isLast ? (1.0 - currentStart) : pctAffiliatedW;
    columns.pctAffiliated = { start: currentStart, width: width };
    currentStart += width;
  }
  
  if (hasChart) {
    // Chart column extends to edge of table
    const chartW = 1.0 - currentStart;
    columns.chart = { start: currentStart, width: chartW };
  }

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    
    // Build headers dynamically based on which columns are visible
    let headers = [];
    
    // Name column (always shown)
    if (columns.name) {
      headers.push({ 
        text: isOrganizations ? 'ORGANIZATION' : 'PROVIDER', 
        col: 'name' 
      });
    }
    
    // Credential column (providers only)
    if (columns.credential) {
      headers.push({ text: 'CRED', col: 'credential' });
    }
    
    // Specialty/Facility Type column
    if (showSpecialtyColumn && columns.specialty) {
      headers.push({ 
        text: isOrganizations ? 'FACILITY TYPE' : 'SPECIALTY', 
        col: 'specialty' 
      });
    }
    
    // Organization column (providers only)
    if (showOrganizationColumn && columns.organization) {
      headers.push({ text: 'ORGANIZATION', col: 'organization' });
    }
    
    // % Affiliated column
    if (showAffiliatedPct && columns.pctAffiliated) {
      headers.push({ text: '% AFFILIATED', col: 'pctAffiliated' });
    }
    
    // Chart column
    if (showChart && columns.chart) {
      headers.push({ text: 'PATIENTS', col: 'chart' });
    }
    
    headers.forEach(header => {
      if (!columns[header.col]) return;
      const colX = startX + columns[header.col].start * tableWidth + 5;
      const colWidth = columns[header.col].width * tableWidth - 10;
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw a single row ===
  const drawRow = (rowData, index) => {
    const totalShared = parseInt(rowData.shared_patients ?? rowData.total ?? 0, 10) || 0;
    const rawAffiliated = rowData.shared_patients_affiliated ?? rowData.affiliated_totals;
    const affiliatedShared = (rawAffiliated === null || rawAffiliated === undefined) 
      ? 0 
      : (parseInt(rawAffiliated, 10) || 0);
    
    const affiliatedPct = totalShared > 0 ? (affiliatedShared / totalShared) * 100 : 0;
    
    // Get display values
    let displayName = '';
    let isAffiliated = false;
    let orgName = '';
    let specialty = '';
    
    if (isOrganizations) {
      const org = rowData.organization || rowData.dataValues?.organization || {};
      const rawName = org.name || org.dataValues?.name || rowData.name || 'Unknown Organization';
      displayName = rawName.toUpperCase();  // ALL CAPS for org names
      isAffiliated = rowData.affiliated === true || 
                    org.is_affiliated === true || 
                    (org.affiliated?.length > 0);
      // Use facility_type for organizations - check nested org first since that's where it comes from
      const rawFacilityType = org.facility_type || org.dataValues?.facility_type ||
                          rowData.facility_type || rowData.dataValues?.facility_type || '';
      specialty = toTitleCase(rawFacilityType);
    } else {
      const firstName = rowData.first_name || rowData.provider?.first_name || '';
      const lastName = rowData.last_name || rowData.provider?.last_name || '';
      const rawName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
      displayName = rawName.toUpperCase();  // ALL CAPS for provider names
      isAffiliated = rowData.is_affiliated ?? rowData.affiliated ?? rowData.provider?.is_affiliated ?? false;
      const rawOrg = rowData.organization_name || rowData.provider?.location?.organization?.name || '';
      orgName = toTitleCase(rawOrg);
      // Get taxonomy from various possible locations in the data structure
      const taxonomy = rowData.taxonomy || rowData.dataValues?.taxonomy || rowData.provider?.taxonomy;
      if (taxonomy) {
        specialty = SpecialtyUtil.getNameFromTaxonomy(taxonomy) || '';
      } else {
        // Fallback to other specialty fields
        specialty = toTitleCase(rowData.provider?.specialty || rowData.specialty || '');
      }
    }
    
    // Get credential for providers
    const credential = !isOrganizations ? (rowData.provider?.credential || rowData.credential || '') : '';
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Icon column ===
    if (showIcon && columns.icon) {
      const iconX = startX + columns.icon.start * tableWidth + 5;
      if (isAffiliated) {
        drawAffiliatedHubIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, affiliatedBarColor);
      }
    }
    
    // === Name column ===
    if (showName && columns.name) {
      const nameX = startX + columns.name.start * tableWidth + 5;
      doc.setFontSize(nameFontSize);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(33, 37, 41);
      const nameColWidth = columns.name.width * tableWidth - 10;
      const nameToDraw = truncateText(doc, displayName, nameColWidth);
      doc.text(nameToDraw, nameX, textY);
    }
    
    // === Credential column (providers only) ===
    if (columns.credential) {
      const credX = startX + columns.credential.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const credColWidth = columns.credential.width * tableWidth - 10;
      const credToDraw = truncateText(doc, credential || '-', credColWidth);
      doc.text(credToDraw, credX, textY);
    }
    
    // === Specialty column (conditional) ===
    if (showSpecialtyColumn && columns.specialty) {
      const specialtyX = startX + columns.specialty.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const specColWidth = columns.specialty.width * tableWidth - 10;
      const specToDraw = truncateText(doc, specialty || '-', specColWidth);
      doc.text(specToDraw, specialtyX, textY);
    }
    
    // === Organization column (only for providers) ===
    if (showOrganizationColumn && columns.organization) {
      const orgX = startX + columns.organization.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const orgColWidth = columns.organization.width * tableWidth - 10;
      const orgToDraw = truncateText(doc, orgName || '-', orgColWidth);
      doc.text(orgToDraw, orgX, textY);
    }
    
    // === % Affiliated column (right aligned with padding) ===
    if (showAffiliatedPct && columns.pctAffiliated) {
      const pctColWidth = columns.pctAffiliated.width * tableWidth;
      const pctX = startX + columns.pctAffiliated.start * tableWidth + pctColWidth - 10;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const pctText = `${affiliatedPct.toFixed(2)}%`;
      doc.text(pctText, pctX, textY, { align: 'right' });
    }
    
    // === Chart column (stacked bar with labels) ===
    if (showChart && columns.chart) {
      const chartX = startX + columns.chart.start * tableWidth + 5;
      const barWidth = columns.chart.width * tableWidth - 10;
      const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
      const barY = currentY + (rowHeight - barHeight) / 2;
      const barRadius = 2;  // Subtle rounded corners
      
      // Scale bar to max value
      const scale = maxValue > 0 ? (totalShared / maxValue) : 0;
      const scaledBarWidth = barWidth * scale;
      
      if (scaledBarWidth > 0) {
        const actualBarWidth = Math.max(scaledBarWidth, barRadius * 2);
        const affiliatedWidth = (affiliatedPct / 100) * actualBarWidth;
        
        // Draw full bar with non-affiliated color (rounded)
        doc.setFillColor(...nonAffiliatedBarColor);
        doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
        
        // Draw affiliated portion (blue) on the left
        if (affiliatedWidth > 0 && affiliatedPct > 0) {
          doc.setFillColor(...affiliatedBarColor);
          if (affiliatedPct >= 100) {
            doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
          } else {
            // Draw left portion with left rounding only
            doc.roundedRect(chartX, barY, affiliatedWidth + barRadius, barHeight, barRadius, barRadius, 'F');
            // Square off the right edge
            doc.rect(chartX + affiliatedWidth, barY, barRadius, barHeight, 'F');
          }
        }
      }
    }
    
    currentY += rowHeight;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = data.slice(0, maxRows);
  rowsToRender.forEach((rowData, index) => {
    drawRow(rowData, index);
  });

  return currentY + 10;
};

/**
 * Draws the Shared Patients chart in table format.
 *
 * Mirrors the on-screen Shared Patients view (ClaimsNetwork). One row per
 * provider with shared patients with the central provider. Single percentage
 * column whose header flips based on Before/After direction:
 *   - After  (filters.before === false): "% [CENTRAL] -> THEM"
 *   - Before (filters.before === true):  "% THEIRS -> [CENTRAL]"
 *
 * Expects rows in the shape produced by ClaimsNetwork.render():
 *   {
 *     x: "LastName, FirstName",
 *     y: number,         // shared patient count
 *     pct1: number,      // natural-direction percentage
 *     affiliated: bool,
 *     provider: { first_name, last_name, taxonomy, location: { organization: { name } } },
 *   }
 *
 * @param {jsPDF} doc
 * @param {Array} data
 * @param {Object} options
 * @returns {number} New Y position after chart
 */
export const drawSharedPatientsChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,
    pdfType = 'printable',
    filters = {},
    selectedColumns = null,
  } = options;

  const pageWidth = doc.internal.pageSize.width;
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;

  // Slightly shorter rows than the other charts so a full page of rows
  // (25 printable / 10 slideshow) still fits on page 1 alongside the filter
  // strip — keeps a default-size export on a single page.
  const rowHeight = pdfType === 'slideshow' ? 27 : 25;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;

  const affiliatedBarColor = AFFILIATION_COLORS.affiliated;
  const nonAffiliatedBarColor = AFFILIATION_COLORS.nonAffiliated;
  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];

  let currentY = y;

  const before = filters.before === true;
  const centralProvider = filters.provider || {};
  const centralLast = (centralProvider.last_name || '').toString();
  const centralLabel = centralLast ? centralLast.toUpperCase() : 'CENTRAL';

  // Two percentage columns when a specific service line is selected. They
  // describe the same shared-patient population with different denominators:
  //   pctW = shared / central provider's total (in this service line)
  //   pctP = shared / row provider's total (in this service line)
  // pct1 vs pct2 map to W/P differently based on Before/After:
  //   After  (before=false): pct1 -> pctW, pct2 -> pctP
  //   Before (before=true):  pct2 -> pctW, pct1 -> pctP
  const pctWHeader = `% OF ${centralLabel}'S`;
  const pctPHeader = `% OF PROVIDER'S`;

  // Column visibility
  const showIcon = selectedColumns ? selectedColumns.icon !== false : true;
  const showName = selectedColumns ? selectedColumns.name !== false : true;
  const showSpecialtyCol = selectedColumns ? selectedColumns.specialty !== false : true;
  const showOrgCol = selectedColumns ? selectedColumns.organization !== false : true;
  const showSharedPatients = selectedColumns ? selectedColumns.patients !== false : true;
  const showChart = selectedColumns ? selectedColumns.chart !== false : true;

  // The percentage columns are only meaningful when a specific service line is
  // selected — the backend's CLAIMS_NETWORK query aggregates rows by
  // SUM(shared_patients) when serviceline === 'all' and drops pct1/pct2.
  // Auto-hide both columns if no row has numeric pct values to avoid a wall
  // of "-" placeholders.
  const pctSource = allData || data;
  const hasAnyPct = pctSource.some((r) => {
    const a = r && r.pct1;
    const b = r && r.pct2;
    const aValid = a !== null && a !== undefined && !Number.isNaN(parseFloat(a));
    const bValid = b !== null && b !== undefined && !Number.isNaN(parseFloat(b));
    return aValid || bValid;
  });
  const showPct = (selectedColumns ? selectedColumns.pct !== false : true) && hasAnyPct;

  // Single specialty filtered? Hide the specialty column to save space.
  const hasSingleSpecialty = (
    (filters.specialty && Array.isArray(filters.specialty) && filters.specialty.length === 1) ||
    (typeof filters.specialty === 'string' && filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') ||
    (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length === 1)
  );
  const showSpecialtyColumn = showSpecialtyCol && !hasSingleSpecialty;

  const getCount = (row) => {
    const raw = row.y ?? row.shared_patients ?? row.sum_shared_patients ?? row.total ?? 0;
    return parseInt(raw, 10) || 0;
  };

  const dataForMax = allData || data;
  // No artificial floor — let the longest bar fill the chart column so bars
  // have visual presence even when the absolute counts are small.
  const maxValue = dataForMax.reduce((max, row) => Math.max(max, getCount(row)), 0) || 1;

  // Column widths (fractions of tableWidth)
  const iconW = showIcon ? 0.03 : 0;
  // Patients column = just enough room for icon + 3-digit count
  // (avoids empty whitespace to the LEFT of the count, which made the
  // "SHARED PATIENTS" header visually float away from the data).
  const sharedPatientsW = showSharedPatients ? 0.05 : 0;
  const pctColW = showPct ? 0.11 : 0;          // per-column width (two columns)
  const pctTotalW = pctColW * 2;
  // Chart kept narrow so the "SHARED PATIENTS" header sits visually close to
  // the count + bar, instead of spanning a huge empty stretch of column.
  const chartReservedW = showChart ? (showPct ? 0.11 : 0.14) : 0;

  // Text columns share the leftover space.
  const visibleTextCols = [showName, showSpecialtyColumn, showOrgCol].filter(Boolean).length;
  const reservedRightSide = sharedPatientsW + pctTotalW + chartReservedW;
  const textAvailable = Math.max(0, 1.0 - iconW - reservedRightSide);

  let namePct = 0;
  let specialtyPct = 0;
  let orgPct = 0;
  if (visibleTextCols === 1) {
    if (showName) namePct = textAvailable;
    else if (showSpecialtyColumn) specialtyPct = textAvailable;
    else if (showOrgCol) orgPct = textAvailable;
  } else if (visibleTextCols === 2) {
    if (showName && showSpecialtyColumn) {
      namePct = textAvailable * 0.6;
      specialtyPct = textAvailable * 0.4;
    } else if (showName && showOrgCol) {
      namePct = textAvailable * 0.5;
      orgPct = textAvailable * 0.5;
    } else if (showSpecialtyColumn && showOrgCol) {
      specialtyPct = textAvailable * 0.4;
      orgPct = textAvailable * 0.6;
    }
  } else if (visibleTextCols === 3) {
    namePct = textAvailable * 0.42;
    specialtyPct = textAvailable * 0.22;
    orgPct = textAvailable * 0.36;
  }

  // Build columns dynamically.
  const columns = {};
  let cursor = 0;
  if (showIcon) {
    columns.icon = { start: cursor, width: iconW };
    cursor += iconW;
  }
  if (showName) {
    columns.name = { start: cursor, width: namePct };
    cursor += namePct;
  }
  if (showSpecialtyColumn) {
    columns.specialty = { start: cursor, width: specialtyPct };
    cursor += specialtyPct;
  }
  if (showOrgCol) {
    columns.organization = { start: cursor, width: orgPct };
    cursor += orgPct;
  }
  // Right-side order: pctW, pctP, then a combined "Shared Patients" group
  // (count + bar) so the count sits next to the bar under one shared header.
  if (showPct) {
    columns.pctW = { start: cursor, width: pctColW };
    cursor += pctColW;
    columns.pctP = { start: cursor, width: pctColW };
    cursor += pctColW;
  }
  if (showSharedPatients) {
    columns.patients = { start: cursor, width: sharedPatientsW };
    cursor += sharedPatientsW;
  }
  if (showChart) {
    columns.chart = { start: cursor, width: Math.max(0, 1.0 - cursor) };
  }

  // === Headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');

    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);

    const headerY = currentY + headerHeight / 2 + 3;
    const headers = [];
    if (columns.name) headers.push({ text: 'PROVIDER', col: 'name', align: 'left' });
    if (columns.specialty) headers.push({ text: 'SPECIALTY', col: 'specialty', align: 'left' });
    if (columns.organization) headers.push({ text: 'ORGANIZATION', col: 'organization', align: 'left' });
    if (columns.pctW) headers.push({ text: pctWHeader, col: 'pctW', align: 'right' });
    if (columns.pctP) headers.push({ text: pctPHeader, col: 'pctP', align: 'right' });

    headers.forEach((header) => {
      const colDef = columns[header.col];
      if (!colDef || colDef.width <= 0) return;
      const colWidth = colDef.width * tableWidth;
      if (header.align === 'right') {
        const colX = startX + colDef.start * tableWidth + colWidth - 5;
        const text = truncateText(doc, header.text, colWidth - 10);
        doc.text(text, colX, headerY, { align: 'right' });
      } else {
        const colX = startX + colDef.start * tableWidth + 5;
        const text = truncateText(doc, header.text, colWidth - 10);
        doc.text(text, colX, headerY);
      }
    });

    // Combined "SHARED PATIENTS" header spanning patients + chart columns
    // (or whichever of those two is present). Left-aligned, sits above the
    // count where the eye naturally lands first.
    const patientsCol = columns.patients;
    const chartCol = columns.chart;
    if (patientsCol || chartCol) {
      const spanStart = patientsCol ? patientsCol.start : chartCol.start;
      const spanWidth = (chartCol
        ? chartCol.start + chartCol.width
        : patientsCol.start + patientsCol.width) - spanStart;
      const spanX = startX + spanStart * tableWidth + 5;
      const label = truncateText(doc, 'SHARED PATIENTS', spanWidth * tableWidth - 10);
      doc.text(label, spanX, headerY);
    }

    currentY += headerHeight + 2;
  };

  // === Row ===
  const drawRow = (rowData, index) => {
    const totalShared = getCount(rowData);
    const provider = rowData.provider || {};
    const firstName = provider.first_name || rowData.first_name || '';
    const lastName = provider.last_name || rowData.last_name || '';
    const rawName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
    const fallbackName = (rowData.x || 'Unknown Provider').toString();
    const displayName = (rawName || fallbackName).toUpperCase();

    const isAffiliated = rowData.affiliated === true ||
      rowData.is_affiliated === true ||
      provider.is_affiliated === true;

    const rawOrg = provider.location?.organization?.name ||
      rowData.organization_name || '';
    const orgName = abbreviateOrgName(toTitleCase(rawOrg));

    let specialty = '';
    const taxonomy = provider.taxonomy || rowData.taxonomy || rowData.dataValues?.taxonomy;
    if (taxonomy) {
      specialty = SpecialtyUtil.getNameFromTaxonomy(taxonomy) || '';
    } else if (rowData.specialty) {
      specialty = toTitleCase(rowData.specialty);
    }
    specialty = abbreviateSpecialty(specialty);

    // Map pct1/pct2 -> pctW/pctP based on direction:
    //   After mode  (before=false): pct1 = % of W's, pct2 = % of P's
    //   Before mode (before=true):  pct2 = % of W's, pct1 = % of P's
    const rawPctW = before ? rowData.pct2 : rowData.pct1;
    const rawPctP = before ? rowData.pct1 : rowData.pct2;
    const fmtPct = (v) => (v === null || v === undefined || Number.isNaN(parseFloat(v)))
      ? '-'
      : `${parseFloat(v).toFixed(1)}%`;
    const pctWText = fmtPct(rawPctW);
    const pctPText = fmtPct(rawPctP);

    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');

    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;

    // Icon
    if (columns.icon && isAffiliated) {
      const iconX = startX + columns.icon.start * tableWidth + 5;
      drawAffiliatedHubIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, affiliatedBarColor);
    }

    // Name (ALL CAPS)
    if (columns.name) {
      const nameX = startX + columns.name.start * tableWidth + 5;
      doc.setFontSize(nameFontSize);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(33, 37, 41);
      const colWidth = columns.name.width * tableWidth - 10;
      doc.text(truncateText(doc, displayName, colWidth), nameX, textY);
    }

    // Specialty
    if (columns.specialty) {
      const colX = startX + columns.specialty.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const colWidth = columns.specialty.width * tableWidth - 10;
      doc.text(truncateText(doc, specialty || '-', colWidth), colX, textY);
    }

    // Organization
    if (columns.organization) {
      const colX = startX + columns.organization.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const colWidth = columns.organization.width * tableWidth - 10;
      doc.text(truncateText(doc, orgName || '-', colWidth), colX, textY);
    }

    // Shared patients (person icon + count) — right-aligned within its column
    // so the count sits flush against the bar in the chart column.
    if (columns.patients) {
      const colStart = startX + columns.patients.start * tableWidth;
      const colWidth = columns.patients.width * tableWidth;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const countText = totalShared > 0 ? totalShared.toLocaleString() : '<11';
      const countTextWidth = doc.getTextWidth(countText);
      // Right edge: 2px before the bar column starts
      const textRightX = colStart + colWidth - 2;
      const textX = textRightX - countTextWidth;
      const iconX = textX - iconSize - 2;
      drawPersonIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, [156, 163, 175]);
      doc.text(countText, textX, textY);
    }

    // Percentages (right aligned). Two columns: pctW and pctP.
    if (columns.pctW) {
      const colWidth = columns.pctW.width * tableWidth;
      const colX = startX + columns.pctW.start * tableWidth + colWidth - 8;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      doc.text(pctWText, colX, textY, { align: 'right' });
    }
    if (columns.pctP) {
      const colWidth = columns.pctP.width * tableWidth;
      const colX = startX + columns.pctP.start * tableWidth + colWidth - 8;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      doc.text(pctPText, colX, textY, { align: 'right' });
    }

    // Chart bar (single solid bar, colored by affiliation).
    // Zero left-padding so the bar begins flush with the count column on the
    // left, making them read as a single "Shared Patients" unit.
    if (columns.chart) {
      const chartLeftPad = columns.patients ? 3 : 5;
      const chartRightPad = 5;
      const chartX = startX + columns.chart.start * tableWidth + chartLeftPad;
      const barWidth = columns.chart.width * tableWidth - chartLeftPad - chartRightPad;
      const barHeight = pdfType === 'slideshow' ? 20 : 18;
      const barY = currentY + (rowHeight - barHeight) / 2;
      const barRadius = 2;
      const scale = maxValue > 0 ? (totalShared / maxValue) : 0;
      const scaledBarWidth = barWidth * scale;
      if (scaledBarWidth > 0) {
        const actualBarWidth = Math.max(scaledBarWidth, barRadius * 2);
        const barColor = isAffiliated ? affiliatedBarColor : nonAffiliatedBarColor;
        doc.setFillColor(...barColor);
        doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
      }
    }

    currentY += rowHeight;
  };

  drawHeaders();
  data.slice(0, maxRows).forEach((rowData, index) => drawRow(rowData, index));

  return currentY + 10;
};

/**
 * Draws the Shared Patients drill-down chart (one provider pair, broken out
 * by service line). Used when a provider box is clicked in ClaimsNetwork.
 *
 * Rows are service lines shared between the central provider and the clicked
 * provider. Two percentage columns, named after the two specific providers:
 *   pctCentral  = shared / central provider's total (in that service line)
 *   pctProvider = shared / clicked provider's total (in that service line)
 * pct1/pct2 map to central/clicked based on Before/After (same as the main
 * chart). Single solid bar colored by the clicked provider's affiliation.
 *
 * Expected row shape (from ClaimsNetwork drill-down, ~line 989):
 *   { x: serviceLineName, y: count, pct1, pct2, serviceline }
 *
 * options.filters: { provider (central), provider2 (clicked), before, affiliated }
 */
export const drawSharedPatientsServiceLineChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,
    pdfType = 'printable',
    filters = {},
    selectedColumns = null,
  } = options;

  const pageWidth = doc.internal.pageSize.width;
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;

  const rowHeight = pdfType === 'slideshow' ? 27 : 25;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;

  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];

  let currentY = y;

  const before = filters.before === true;
  const central = filters.provider || {};
  const clicked = filters.provider2 || {};
  const centralLabel = (central.last_name || 'CENTRAL').toString().toUpperCase();
  const clickedLabel = (clicked.last_name || 'PROVIDER').toString().toUpperCase();
  const pctCentralHeader = `% OF ${centralLabel}'S`;
  const pctClickedHeader = `% OF ${clickedLabel}'S`;
  // All drill-down bars use the Doctivity green (#00a887).
  const barColor = [0, 168, 135];

  // Column visibility
  const showName = selectedColumns ? selectedColumns.name !== false : true;
  const showSharedPatients = selectedColumns ? selectedColumns.patients !== false : true;
  const showPct = selectedColumns ? selectedColumns.pct !== false : true;
  const showChart = selectedColumns ? selectedColumns.chart !== false : true;

  const getCount = (row) => {
    const raw = row.y ?? row.shared_patients ?? row.sum_shared_patients ?? row.total ?? 0;
    return parseInt(raw, 10) || 0;
  };

  const dataForMax = allData || data;
  const maxValue = dataForMax.reduce((max, row) => Math.max(max, getCount(row)), 0) || 1;

  // Column widths
  const pctColW = showPct ? 0.13 : 0;
  const sharedPatientsW = showSharedPatients ? 0.05 : 0;
  const chartReservedW = showChart ? 0.16 : 0;
  const nameW = Math.max(0, 1.0 - (pctColW * 2) - sharedPatientsW - chartReservedW);

  const columns = {};
  let cursor = 0;
  if (showName) {
    columns.name = { start: cursor, width: nameW };
    cursor += nameW;
  }
  if (showPct) {
    columns.pctCentral = { start: cursor, width: pctColW };
    cursor += pctColW;
    columns.pctClicked = { start: cursor, width: pctColW };
    cursor += pctColW;
  }
  if (showSharedPatients) {
    columns.patients = { start: cursor, width: sharedPatientsW };
    cursor += sharedPatientsW;
  }
  if (showChart) {
    columns.chart = { start: cursor, width: Math.max(0, 1.0 - cursor) };
  }

  // === Headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');

    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);

    const headerY = currentY + headerHeight / 2 + 3;
    if (columns.name) {
      doc.text(truncateText(doc, 'SERVICE LINE', columns.name.width * tableWidth - 10),
        startX + columns.name.start * tableWidth + 5, headerY);
    }
    if (columns.pctCentral) {
      const w = columns.pctCentral.width * tableWidth;
      doc.text(truncateText(doc, pctCentralHeader, w - 10),
        startX + columns.pctCentral.start * tableWidth + w - 5, headerY, { align: 'right' });
    }
    if (columns.pctClicked) {
      const w = columns.pctClicked.width * tableWidth;
      doc.text(truncateText(doc, pctClickedHeader, w - 10),
        startX + columns.pctClicked.start * tableWidth + w - 5, headerY, { align: 'right' });
    }
    // Combined "SHARED PATIENTS" header spanning count + chart, left-aligned
    const patientsCol = columns.patients;
    const chartCol = columns.chart;
    if (patientsCol || chartCol) {
      const spanStart = patientsCol ? patientsCol.start : chartCol.start;
      doc.text(truncateText(doc, 'SHARED PATIENTS', 0.21 * tableWidth - 10),
        startX + spanStart * tableWidth + 5, headerY);
    }

    currentY += headerHeight + 2;
  };

  // === Row ===
  const drawRow = (rowData, index) => {
    const totalShared = getCount(rowData);
    const rawName = rowData.x || rowData.serviceline?.name || rowData.name || 'Unknown';
    const displayName = toTitleCase(rawName.toString());

    // Map pct1/pct2 -> central/clicked based on direction (same as main chart)
    const rawPctCentral = before ? rowData.pct2 : rowData.pct1;
    const rawPctClicked = before ? rowData.pct1 : rowData.pct2;
    const fmtPct = (v) => (v === null || v === undefined || Number.isNaN(parseFloat(v)))
      ? '-'
      : `${parseFloat(v).toFixed(1)}%`;
    const pctCentralText = fmtPct(rawPctCentral);
    const pctClickedText = fmtPct(rawPctClicked);

    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');

    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;

    // Service line name
    if (columns.name) {
      const colX = startX + columns.name.start * tableWidth + 5;
      doc.setFontSize(nameFontSize);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(33, 37, 41);
      doc.text(truncateText(doc, displayName, columns.name.width * tableWidth - 10), colX, textY);
    }

    // % columns (right aligned)
    if (columns.pctCentral) {
      const w = columns.pctCentral.width * tableWidth;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      doc.text(pctCentralText, startX + columns.pctCentral.start * tableWidth + w - 8, textY, { align: 'right' });
    }
    if (columns.pctClicked) {
      const w = columns.pctClicked.width * tableWidth;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      doc.text(pctClickedText, startX + columns.pctClicked.start * tableWidth + w - 8, textY, { align: 'right' });
    }

    // Shared patients count (right-aligned, hugging the bar)
    if (columns.patients) {
      const colStart = startX + columns.patients.start * tableWidth;
      const colWidth = columns.patients.width * tableWidth;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const countText = totalShared > 0 ? totalShared.toLocaleString() : '<11';
      const countTextWidth = doc.getTextWidth(countText);
      const textRightX = colStart + colWidth - 2;
      const textX = textRightX - countTextWidth;
      const personIconX = textX - iconSize - 2;
      drawPersonIcon(doc, personIconX, currentY + (rowHeight - iconSize) / 2, iconSize, [156, 163, 175]);
      doc.text(countText, textX, textY);
    }

    // Bar
    if (columns.chart) {
      const chartLeftPad = columns.patients ? 3 : 5;
      const chartRightPad = 5;
      const chartX = startX + columns.chart.start * tableWidth + chartLeftPad;
      const barWidth = columns.chart.width * tableWidth - chartLeftPad - chartRightPad;
      const barHeight = pdfType === 'slideshow' ? 20 : 18;
      const barY = currentY + (rowHeight - barHeight) / 2;
      const barRadius = 2;
      const scale = maxValue > 0 ? (totalShared / maxValue) : 0;
      const scaledBarWidth = barWidth * scale;
      if (scaledBarWidth > 0) {
        const actualBarWidth = Math.max(scaledBarWidth, barRadius * 2);
        doc.setFillColor(...barColor);
        doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
      }
    }

    currentY += rowHeight;
  };

  drawHeaders();
  data.slice(0, maxRows).forEach((rowData, index) => drawRow(rowData, index));

  return currentY + 10;
};

/**
 * Draws the Shared Affiliation by Service Lines chart in table format
 */
export const drawSharedAffiliationByServiceLineChart = (doc, data, options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent scaling across pages
    showLegend = true,
    pdfType = 'printable',
    selectedColumns = null,  // Optional: column visibility selection from PDFExportDialog
  } = options;

  const settings = getSettingsByType(pdfType);
  const pageWidth = doc.internal.pageSize.width;
  
  // Table dimensions
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;
  
  // Row settings
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  
  // Colors
  const affiliatedBarColor = AFFILIATION_COLORS.affiliated;
  const nonAffiliatedBarColor = AFFILIATION_COLORS.nonAffiliated;
  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];
  
  let currentY = y;
  
  // Column visibility - use selectedColumns if provided, otherwise use defaults
  const showName = selectedColumns ? selectedColumns.name !== false : true;
  const showPatients = selectedColumns ? selectedColumns.patients !== false : true;
  const showAffiliatedPct = selectedColumns ? selectedColumns.affiliatedPct !== false : true;
  const showChart = selectedColumns ? selectedColumns.chart !== false : true;
  
  // Find max value for scaling bars - use allData if provided
  const dataArray = Array.isArray(data) ? data : [];
  const dataForMax = allData || dataArray;
  const maxValue = dataForMax.reduce((max, row) => {
    const total = parseInt(row.shared_patients ?? row.total ?? 0, 10) || 0;
    return Math.max(max, total);
  }, 100);

  // Column layout for Service Lines - dynamic based on visibility
  const nameW = 0.40;
  const patientsW = showPatients ? 0.12 : 0;
  const pctAffiliatedW = showAffiliatedPct ? 0.15 : 0;
  
  // Build columns dynamically
  let columns = {};
  let currentStart = 0;
  
  // Name column - only include if checked
  if (showName) {
    columns.name = { start: currentStart, width: nameW };
    currentStart += nameW;
  }
  
  if (showPatients) {
    columns.patients = { start: currentStart, width: patientsW };
    currentStart += patientsW;
  }
  
  if (showAffiliatedPct) {
    // If this is the last column, extend to edge
    const isLast = !showChart;
    const width = isLast ? (1.0 - currentStart) : pctAffiliatedW;
    columns.pctAffiliated = { start: currentStart, width: width };
    currentStart += width;
  }
  
  if (showChart) {
    // Chart column extends to edge
    const chartW = 1.0 - currentStart;
    columns.chart = { start: currentStart, width: chartW };
  }

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    
    // Build headers dynamically
    let headers = [];
    if (columns.name) {
      headers.push({ text: 'SERVICE LINE', col: 'name' });
    }
    if (showPatients && columns.patients) {
      headers.push({ text: 'PATIENTS', col: 'patients' });
    }
    if (showAffiliatedPct && columns.pctAffiliated) {
      headers.push({ text: '% AFFILIATED', col: 'pctAffiliated' });
    }
    if (showChart && columns.chart) {
      headers.push({ text: 'PATIENTS', col: 'chart' });
    }
    
    headers.forEach(header => {
      if (!columns[header.col]) return;
      const colX = startX + columns[header.col].start * tableWidth + 5;
      const colWidth = columns[header.col].width * tableWidth - 10;
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw a single row ===
  const drawRow = (rowData, index) => {
    const totalShared = parseInt(rowData.shared_patients ?? rowData.total ?? 0, 10) || 0;
    const rawAffiliated = rowData.shared_patients_affiliated ?? rowData.affiliated_totals;
    const affiliatedShared = (rawAffiliated === null || rawAffiliated === undefined) 
      ? 0 
      : (parseInt(rawAffiliated, 10) || 0);
    
    const affiliatedPct = totalShared > 0 ? (affiliatedShared / totalShared) * 100 : 0;
    
    const rawServiceLine = rowData.name || rowData.serviceline_name || rowData.service_line_name || rowData.label || `Service Line ${index + 1}`;
    const serviceLineName = toTitleCase(rawServiceLine);
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Service Line Name column ===
    if (showName && columns.name) {
      const nameX = startX + columns.name.start * tableWidth + 5;
      doc.setFontSize(nameFontSize);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(33, 37, 41);
      const nameColWidth = columns.name.width * tableWidth - 10;
      const nameToDraw = truncateText(doc, serviceLineName, nameColWidth);
      doc.text(nameToDraw, nameX, textY);
    }
    
    // === Patients column ===
    if (showPatients && columns.patients) {
      const patientsX = startX + columns.patients.start * tableWidth + 2;
      drawPersonIcon(doc, patientsX, currentY + (rowHeight - iconSize) / 2, iconSize, [156, 163, 175]);
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const countText = totalShared > 0 ? totalShared.toLocaleString() : '<11';
      doc.text(countText, patientsX + iconSize + 2, textY);
    }
    
    // === % Affiliated column (right aligned with padding) ===
    if (showAffiliatedPct && columns.pctAffiliated) {
      const pctColWidth = columns.pctAffiliated.width * tableWidth;
      const pctX = startX + columns.pctAffiliated.start * tableWidth + pctColWidth - 10;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const pctText = `${affiliatedPct.toFixed(2)}%`;
      doc.text(pctText, pctX, textY, { align: 'right' });
    }
    
    // === Chart column (stacked bar) ===
    if (showChart && columns.chart) {
      const chartX = startX + columns.chart.start * tableWidth + 5;
      const barWidth = columns.chart.width * tableWidth - 10;
      const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
      const barY = currentY + (rowHeight - barHeight) / 2;
      const barRadius = 2;  // Subtle rounded corners
      
      // Scale bar to max value
      const scale = maxValue > 0 ? (totalShared / maxValue) : 0;
      const scaledBarWidth = barWidth * scale;
      
      if (scaledBarWidth > 0) {
        const actualBarWidth = Math.max(scaledBarWidth, barRadius * 2);
        const affiliatedWidth = (affiliatedPct / 100) * actualBarWidth;
        
        // Draw full bar with non-affiliated color (rounded)
        doc.setFillColor(...nonAffiliatedBarColor);
        doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
        
        // Draw affiliated portion (blue) on the left
        if (affiliatedWidth > 0 && affiliatedPct > 0) {
          doc.setFillColor(...affiliatedBarColor);
          if (affiliatedPct >= 100) {
            doc.roundedRect(chartX, barY, actualBarWidth, barHeight, barRadius, barRadius, 'F');
          } else {
            // Draw left portion with left rounding only
            doc.roundedRect(chartX, barY, affiliatedWidth + barRadius, barHeight, barRadius, barRadius, 'F');
            // Square off the right edge
            doc.rect(chartX + affiliatedWidth, barY, barRadius, barHeight, 'F');
          }
        }
      }
    }
    
    currentY += rowHeight;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = dataArray.slice(0, maxRows);
  rowsToRender.forEach((rowData, index) => {
    drawRow(rowData, index);
  });

  return currentY + 10;
};

/**
 * Draws the Payors stacked horizontal bar chart
 */
export const drawPayorChart = (doc, data, payors = [], options = {}) => {
  const {
    x = null,
    y = 80,
    width = null,
    maxRows = 200,
    allData = null,  // Optional: pass all data for consistent scaling across pages
    showLegend = true,
    claimsType = 'providers',
    pdfType = 'printable',
    filters = {},
    selectedColumns = null,  // Optional: column visibility selection from PDFExportDialog
  } = options;

  const settings = getSettingsByType(pdfType);
  const pageWidth = doc.internal.pageSize.width;
  
  // Table dimensions
  const startX = x || 20;
  const tableWidth = width || pageWidth - 40;
  
  // Row settings
  const rowHeight = pdfType === 'slideshow' ? 30 : 26;
  const headerHeight = pdfType === 'slideshow' ? 24 : 20;
  const fontSize = pdfType === 'slideshow' ? 10 : 8;
  const headerFontSize = pdfType === 'slideshow' ? 8 : 6;
  const nameFontSize = pdfType === 'slideshow' ? 11 : 9;
  
  // Colors
  const headerBgColor = [235, 241, 248];
  const headerTextColor = [100, 116, 139];
  const rowBgColor = [255, 255, 255];
  const altRowBgColor = [243, 247, 251];
  const affiliatedBarColor = AFFILIATION_COLORS.affiliated;
  
  let currentY = y;
  
  const isOrganizations = claimsType === 'organizations';
  
  // Column visibility - use selectedColumns if provided, otherwise use defaults
  const showIcon = selectedColumns ? selectedColumns.icon !== false : true;
  const showName = selectedColumns ? selectedColumns.name !== false : true;
  const showCredential = selectedColumns ? selectedColumns.credential === true : false;  // Default off
  const showSpecialtyCol = selectedColumns ? (selectedColumns.specialty !== false && selectedColumns.facilityType !== false) : true;
  const showOrgCol = selectedColumns ? selectedColumns.organization !== false : true;
  const showChart = selectedColumns ? selectedColumns.chart !== false : true;
  
  // Check if single specialty is filtered (hide specialty column if so)
  const hasSingleSpecialty = (
    (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') ||
    (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length === 1)
  );
  const showSpecialtyColumn = showSpecialtyCol && (!hasSingleSpecialty || isOrganizations);
  const showOrganizationColumn = !isOrganizations && showOrgCol;
  
  // Get payor colors
  const payorColors = payors.map((payor, idx) => {
    if (payor.color) {
      const rgb = colorToRgb(payor.color);
      if (rgb) return rgb;
    }
    return DEFAULT_PAYOR_COLORS[idx] || DEFAULT_PAYOR_COLORS[3];
  });

  // Column layout for Payors
  // Base widths - these are starting points, columns may expand if others are hidden
  const iconW = showIcon ? 0.03 : 0;
  const credentialW = showCredential && !isOrganizations ? 0.06 : 0;  // Only for providers
  
  // Count visible text columns to distribute space
  const visibleTextCols = [showName, showSpecialtyColumn, showOrganizationColumn].filter(Boolean).length;
  
  let namePct, specialtyPct, orgPct;
  
  if (!showChart) {
    // No chart - text columns should expand to fill space
    const availableWidth = 1.0 - iconW;
    if (visibleTextCols === 0) {
      namePct = 0;
      specialtyPct = 0;
      orgPct = 0;
    } else if (visibleTextCols === 1) {
      // Single column gets all the space
      namePct = showName ? availableWidth : 0;
      specialtyPct = showSpecialtyColumn ? availableWidth : 0;
      orgPct = showOrganizationColumn ? availableWidth : 0;
    } else if (visibleTextCols === 2) {
      // Two columns split the space
      if (showName && showSpecialtyColumn) {
        namePct = availableWidth * 0.5;
        specialtyPct = availableWidth * 0.5;
        orgPct = 0;
      } else if (showName && showOrganizationColumn) {
        namePct = availableWidth * 0.45;
        specialtyPct = 0;
        orgPct = availableWidth * 0.55;
      } else {
        // specialty and org only
        namePct = 0;
        specialtyPct = availableWidth * 0.4;
        orgPct = availableWidth * 0.6;
      }
    } else {
      // All three columns
      namePct = availableWidth * 0.35;
      specialtyPct = availableWidth * 0.25;
      orgPct = availableWidth * 0.40;
    }
  } else {
    // Chart is shown - use normal proportions
    if (isOrganizations) {
      namePct = 0.40;
      specialtyPct = showSpecialtyColumn ? 0.12 : 0;
      orgPct = 0;
    } else {
      namePct = showName ? 0.40 : 0;
      if (showSpecialtyColumn && showOrganizationColumn) {
        specialtyPct = 0.12;
        orgPct = 0.20;
      } else if (showSpecialtyColumn) {
        specialtyPct = 0.12;
        orgPct = 0;
      } else if (showOrganizationColumn) {
        specialtyPct = 0;
        orgPct = 0.20;
      } else {
        specialtyPct = 0;
        orgPct = 0;
      }
    }
  }
  
  // Build column layout dynamically based on visibility
  let columns = {};
  let currentStart = 0;
  
  if (showIcon) {
    columns.icon = { start: currentStart, width: iconW };
    currentStart += iconW;
  }
  
  // Name column - only include if checked
  if (showName) {
    columns.name = { start: currentStart, width: namePct };
    currentStart += namePct;
  }
  
  // Credential column - only for providers
  if (showCredential && !isOrganizations) {
    columns.credential = { start: currentStart, width: credentialW };
    currentStart += credentialW;
  }
  
  if (showSpecialtyColumn) {
    columns.specialty = { start: currentStart, width: specialtyPct };
    currentStart += specialtyPct;
  }
  
  if (showOrganizationColumn) {
    columns.organization = { start: currentStart, width: orgPct };
    currentStart += orgPct;
  }
  
  if (showChart) {
    // Chart column extends to edge of table
    const chartW = 1.0 - currentStart;
    columns.chart = { start: currentStart, width: chartW };
  }

  // === Draw column headers ===
  const drawHeaders = () => {
    doc.setFillColor(...headerBgColor);
    doc.roundedRect(startX, currentY, tableWidth, headerHeight, 4, 4, 'F');
    doc.rect(startX, currentY + headerHeight / 2, tableWidth, headerHeight / 2, 'F');
    
    doc.setFontSize(headerFontSize);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(...headerTextColor);
    
    const headerY = currentY + headerHeight / 2 + 3;
    
    // Build headers dynamically based on which columns are visible
    let headers = [];
    
    // Name column (always shown)
    if (columns.name) {
      headers.push({ 
        text: isOrganizations ? 'ORGANIZATION' : 'PROVIDER', 
        col: 'name' 
      });
    }
    
    // Credential column (providers only)
    if (columns.credential) {
      headers.push({ text: 'CRED', col: 'credential' });
    }
    
    // Specialty/Facility Type column
    if (showSpecialtyColumn && columns.specialty) {
      headers.push({ 
        text: isOrganizations ? 'FACILITY TYPE' : 'SPECIALTY', 
        col: 'specialty' 
      });
    }
    
    // Organization column (providers only)
    if (showOrganizationColumn && columns.organization) {
      headers.push({ text: 'ORGANIZATION', col: 'organization' });
    }
    
    // Chart column
    if (showChart && columns.chart) {
      headers.push({ text: 'PAYOR MIX', col: 'chart' });
    }
    
    headers.forEach(header => {
      if (!columns[header.col]) return;
      const colX = startX + columns[header.col].start * tableWidth + 5;
      const colWidth = columns[header.col].width * tableWidth - 10;
      const headerText = truncateText(doc, header.text, colWidth);
      doc.text(headerText, colX, headerY);
    });
    
    currentY += headerHeight + 2;
  };

  // === Draw legend below headers ===
  const drawPayorLegendRow = () => {
    if (!showLegend || payors.length === 0) return;
    
    const legendHeight = pdfType === 'slideshow' ? 20 : 16;
    const legendY = currentY;
    const legendFontSize = pdfType === 'slideshow' ? 8 : 6;
    const circleRadius = pdfType === 'slideshow' ? 5 : 4;  // Circle radius instead of box size
    
    doc.setFontSize(legendFontSize);
    doc.setFont('Roboto', 'normal');
    
    let legendX = startX + 5;
    
    // Draw each payor legend item with circles
    payors.forEach((payor, idx) => {
      if (idx >= 3) return; // Max 3 payors
      
      const color = payorColors[idx];
      doc.setFillColor(...color);
      // Draw circle: x, y is center
      const circleY = legendY + legendHeight / 2;
      doc.circle(legendX + circleRadius, circleY, circleRadius, 'F');
      
      doc.setTextColor(60, 60, 60);
      const label = payor.name || `Payor ${idx + 1}`;
      const truncatedLabel = label.length > 20 ? label.substring(0, 18) + '...' : label;
      doc.text(truncatedLabel, legendX + circleRadius * 2 + 4, legendY + legendHeight / 2 + 2);
      
      legendX += circleRadius * 2 + doc.getTextWidth(truncatedLabel) + 15;
    });
    
    // Add "Other" legend item with circle
    doc.setFillColor(...DEFAULT_PAYOR_COLORS[3]);
    const circleY = legendY + legendHeight / 2;
    doc.circle(legendX + circleRadius, circleY, circleRadius, 'F');
    doc.setTextColor(60, 60, 60);
    doc.text('Other', legendX + circleRadius * 2 + 4, legendY + legendHeight / 2 + 2);
    
    currentY += legendHeight;  // Reduced spacing after legend
  };

  // === Draw a single row ===
  const drawRow = (claim, index) => {
    // Get display values
    let displayName = '';
    let isAffiliated = false;
    let orgName = '';
    let specialty = '';
    
    if (isOrganizations) {
      const org = claim.organization || claim.dataValues?.organization || {};
      const rawName = org.name || org.dataValues?.name || claim.name || 'Unknown Organization';
      displayName = rawName.toUpperCase();  // ALL CAPS for org names
      isAffiliated = org.is_affiliated || org.affiliated?.length > 0;
      // Use facility_type for organizations - check nested org first since that's where it comes from
      const rawFacilityType = org.facility_type || org.dataValues?.facility_type ||
                          claim.facility_type || claim.dataValues?.facility_type || '';
      specialty = toTitleCase(rawFacilityType);
    } else {
      const firstName = claim.provider?.first_name || '';
      const lastName = claim.provider?.last_name || '';
      const rawName = `${lastName}, ${firstName}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '') || 'Unknown Provider';
      displayName = rawName.toUpperCase();  // ALL CAPS for provider names
      isAffiliated = claim.provider?.is_affiliated || claim.provider?.clients?.length > 0;
      const rawOrg = claim.organization_name || claim.provider?.location?.organization?.name || '';
      orgName = toTitleCase(rawOrg);
      // Build taxonomy object in expected format for SpecialtyUtil
      const taxonomy = {
        doctivity_name: claim.specialty_doctivity_name,
        specialization: claim.specialty_specialization,
        classification: claim.specialty_classification
      };
      specialty = SpecialtyUtil.getNameFromTaxonomy(taxonomy);
    }
    
    // Get credential for providers
    const credential = !isOrganizations ? (claim.provider?.credential || claim.credential || '') : '';
    
    const totalClaims = claim.total || claim.total_claims || 0;
    
    // Row background
    const bgColor = index % 2 === 0 ? rowBgColor : altRowBgColor;
    doc.setFillColor(...bgColor);
    doc.rect(startX, currentY, tableWidth, rowHeight, 'F');
    
    const textY = currentY + rowHeight / 2 + 3;
    const iconSize = pdfType === 'slideshow' ? 12 : 10;
    
    // === Icon column ===
    if (showIcon && columns.icon) {
      const iconX = startX + columns.icon.start * tableWidth + 5;
      if (isAffiliated) {
        drawAffiliatedHubIcon(doc, iconX, currentY + (rowHeight - iconSize) / 2, iconSize, affiliatedBarColor);
      }
    }
    
    // === Name column ===
    if (showName && columns.name) {
      const nameX = startX + columns.name.start * tableWidth + 5;
      doc.setFontSize(nameFontSize);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(33, 37, 41);
      const nameColWidth = columns.name.width * tableWidth - 10;
      const nameToDraw = truncateText(doc, displayName, nameColWidth);
      doc.text(nameToDraw, nameX, textY);
    }
    
    // === Credential column (providers only) ===
    if (columns.credential) {
      const credX = startX + columns.credential.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const credColWidth = columns.credential.width * tableWidth - 10;
      const credToDraw = truncateText(doc, credential || '-', credColWidth);
      doc.text(credToDraw, credX, textY);
    }
    
    // === Specialty column (conditional) ===
    if (showSpecialtyColumn && columns.specialty) {
      const specialtyX = startX + columns.specialty.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const specColWidth = columns.specialty.width * tableWidth - 10;
      const specToDraw = truncateText(doc, specialty || '-', specColWidth);
      doc.text(specToDraw, specialtyX, textY);
    }
    
    // === Organization column (only for providers) ===
    if (showOrganizationColumn && columns.organization) {
      const orgX = startX + columns.organization.start * tableWidth + 5;
      doc.setFontSize(fontSize);
      doc.setFont('RobotoLight', 'normal');
      doc.setTextColor(75, 85, 99);
      const orgColWidth = columns.organization.width * tableWidth - 10;
      const orgToDraw = truncateText(doc, orgName || '-', orgColWidth);
      doc.text(orgToDraw, orgX, textY);
    }
    
    // === Chart column (stacked payor bar with labels) ===
    if (showChart && columns.chart) {
      const chartX = startX + columns.chart.start * tableWidth + 5;
      const barWidth = columns.chart.width * tableWidth - 10;
      const barHeight = pdfType === 'slideshow' ? 20 : 18;  // Taller bars
      const barY = currentY + (rowHeight - barHeight) / 2;
      const barRadius = 2;  // Subtle rounded corners
      
      // Calculate payor percentages and collect segment data
      let totalPct = 0;
      const segmentData = [];
    
    for (let i = 0; i < payors.length && i < 3; i++) {
      const pct = parseFloat(claim[`percent_${i}`]) || 0;
      totalPct += pct;
      if (pct > 0) {
        segmentData.push({ pct, color: payorColors[i] });
      }
    }
    
    // "Other" segment
    const otherPct = Math.max(0, 100 - totalPct);
    if (otherPct > 0) {
      segmentData.push({ pct: otherPct, color: DEFAULT_PAYOR_COLORS[3] });
    }
    
    // Draw rounded bar with segments
    const segments = [];
    if (segmentData.length > 0) {
      // Draw full bar with last segment's color (rounded)
      const lastColor = segmentData[segmentData.length - 1].color;
      doc.setFillColor(...lastColor);
      doc.roundedRect(chartX, barY, barWidth, barHeight, barRadius, barRadius, 'F');
      
      // Draw segments from left to right
      let currentBarX = chartX;
      let remainingWidth = barWidth;
      
      for (let i = 0; i < segmentData.length - 1; i++) {
        const seg = segmentData[i];
        const segmentWidth = (seg.pct / 100) * barWidth;
        
        if (segmentWidth > 0) {
          doc.setFillColor(...seg.color);
          if (i === 0) {
            // First segment - rounded on left only
            doc.roundedRect(currentBarX, barY, segmentWidth + barRadius, barHeight, barRadius, barRadius, 'F');
            doc.rect(currentBarX + segmentWidth, barY, barRadius, barHeight, 'F');
          } else {
            // Middle segments - no rounding
            doc.rect(currentBarX, barY, segmentWidth, barHeight, 'F');
          }
          segments.push({ x: currentBarX, width: segmentWidth, pct: seg.pct });
          currentBarX += segmentWidth;
          remainingWidth -= segmentWidth;
        }
      }
      
      // Last segment already drawn as background
      if (segmentData.length > 0) {
        segments.push({ x: currentBarX, width: remainingWidth, pct: segmentData[segmentData.length - 1].pct });
      }
    }
    
    // Draw percentage labels on segments (if enough space)
    // Center vertically: barY + barHeight/2 gives center, then adjust for text baseline
    const labelFontSize = pdfType === 'slideshow' ? 9 : 7;  // Larger font
    const barTextY = barY + (barHeight / 2) + (labelFontSize / 3);  // Better vertical centering
    doc.setFontSize(labelFontSize);
    doc.setFont('Roboto', 'bold');

    segments.forEach((seg, idx) => {
      if (seg.width > 25 && seg.pct >= 5) {
        const isOther = idx === segments.length - 1 && otherPct > 0;
        if (isOther) {
          doc.setTextColor(100, 116, 139);  // #64748B — matches on-screen "Other Payors" label
        } else {
          doc.setTextColor(255, 255, 255);
        }
        const label = `${Math.round(seg.pct)}%`;
        doc.text(label, seg.x + seg.width / 2, barTextY, { align: 'center' });
      }
    });
    }  // End of showChart conditional
    
    currentY += rowHeight;
  };

  // Draw headers
  drawHeaders();
  
  // Draw each row
  const rowsToRender = data.slice(0, maxRows);
  rowsToRender.forEach((claim, index) => {
    drawRow(claim, index);
  });
  
  // Draw legend row at bottom (after all data rows)
  drawPayorLegendRow();

  return currentY + 10;
};

/**
 * Helper to convert various color formats to RGB array
 */
const colorToRgb = (color) => {
  if (!color) return null;
  
  if (Array.isArray(color)) {
    return color;
  }
  
  if (typeof color === 'string') {
    if (color.startsWith('#')) {
      return hexToRgb(color);
    }
    
    const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      return [parseInt(rgbMatch[1], 10), parseInt(rgbMatch[2], 10), parseInt(rgbMatch[3], 10)];
    }
  }
  
  return null;
};

/**
 * Helper to convert hex color to RGB array
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
  }
  const shortResult = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
  if (shortResult) {
    return [
      parseInt(shortResult[1] + shortResult[1], 16),
      parseInt(shortResult[2] + shortResult[2], 16),
      parseInt(shortResult[3] + shortResult[3], 16),
    ];
  }
  return [128, 128, 128];
};

/**
 * Draw a custom "Title and Image" slide
 * @param {Object} doc - jsPDF document
 * @param {Object} options - Slide options
 * @param {string} options.title - Slide title
 * @param {string} options.imageData - Base64 image data (optional)
 * @param {string} options.imageUrl - Image URL (optional)
 * @param {string} options.pdfType - 'printable' or 'slideshow'
 */
export const drawTitleAndImageSlide = async (doc, options = {}) => {
  const {
    title = '',
    subtitle = '',
    imageData = null,
    pdfType = 'printable',
  } = options;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Draw the styled header background
  drawPageHeader(doc, title, subtitle, [], { pdfType });
  
  // If we have an image, draw it centered in the content area
  if (imageData) {
    const containerTop = subtitle ? 85 : 70;
    const containerMargin = 20;
    const availableWidth = pageWidth - (containerMargin * 2) - 20;
    const availableHeight = pageHeight - containerTop - 50;
    
    try {
      // Calculate image dimensions to fit while maintaining aspect ratio
      const img = new Image();
      img.src = imageData;
      
      // Default dimensions if we can't determine from image
      let imgWidth = availableWidth * 0.8;
      let imgHeight = availableHeight * 0.8;
      
      // Center the image
      const imgX = containerMargin + 10 + (availableWidth - imgWidth) / 2;
      const imgY = containerTop + 10 + (availableHeight - imgHeight) / 2;
      
      doc.addImage(imageData, 'PNG', imgX, imgY, imgWidth, imgHeight);
    } catch (e) {
      // If image fails, show placeholder text
      doc.setFont('Roboto', 'normal');
      doc.setFontSize(14);
      doc.setTextColor(150, 150, 150);
      doc.text('Image could not be loaded', pageWidth / 2, pageHeight / 2, { align: 'center' });
    }
  }
  
  return pageHeight;
};

/**
 * Draw a custom "Title and Paragraph" slide with optional bullet points
 * @param {Object} doc - jsPDF document
 * @param {Object} options - Slide options
 * @param {string} options.title - Slide title
 * @param {string} options.content - Paragraph content (use \n for line breaks, • or - at start of line for bullets)
 * @param {string} options.pdfType - 'printable' or 'slideshow'
 */
export const drawTitleAndParagraphSlide = (doc, options = {}) => {
  const {
    title = '',
    subtitle = '',
    content = '',
    pdfType = 'printable',
  } = options;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Draw the styled header background
  drawPageHeader(doc, title, subtitle, [], { pdfType });
  
  // Content area
  const containerTop = subtitle ? 85 : 70;
  const containerMargin = 20;
  const contentX = containerMargin + 15;
  const contentWidth = pageWidth - (containerMargin * 2) - 30;
  let currentY = containerTop + 20;
  
  const fontSize = pdfType === 'slideshow' ? 14 : 11;
  const lineHeight = fontSize * 1.5;
  const bulletIndent = 15;
  
  doc.setFont('Roboto', 'normal');
  doc.setFontSize(fontSize);
  doc.setTextColor(50, 50, 50);
  
  // Split content into lines
  const lines = content.split('\n');
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (!trimmedLine) {
      // Empty line - add spacing
      currentY += lineHeight * 0.5;
      return;
    }
    
    // Check if line is a bullet point
    const isBullet = trimmedLine.startsWith('•') || 
                     trimmedLine.startsWith('-') || 
                     trimmedLine.startsWith('*') ||
                     /^\d+\./.test(trimmedLine);  // Numbered list
    
    let textX = contentX;
    let text = trimmedLine;
    
    if (isBullet) {
      // Draw bullet or number
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
        doc.setFillColor(80, 80, 80);
        doc.circle(contentX + 3, currentY - 3, 2, 'F');
        text = trimmedLine.substring(1).trim();
        textX = contentX + bulletIndent;
      } else {
        // Numbered list - keep the number
        textX = contentX + 5;
      }
    }
    
    // Word wrap the text
    const maxWidth = contentWidth - (isBullet ? bulletIndent : 0);
    const wrappedLines = doc.splitTextToSize(text, maxWidth);
    
    wrappedLines.forEach((wrappedLine, idx) => {
      // Check if we need a new page
      if (currentY > pageHeight - 40) {
        doc.addPage();
        drawPageHeader(doc, title, '', [], { pdfType });
        currentY = containerTop + 20;
      }
      
      // Indent continuation lines for bullets
      const lineX = idx > 0 && isBullet ? textX : textX;
      doc.text(wrappedLine, lineX, currentY);
      currentY += lineHeight;
    });
  });
  
  return pageHeight;
};

/**
 * Draw a blank page with just the styled header (for exec summary, etc.)
 * @param {Object} doc - jsPDF document
 * @param {Object} options - Slide options
 */
export const drawBlankSlide = (doc, options = {}) => {
  const {
    title = '',
    subtitle = '',
    pdfType = 'printable',
  } = options;

  const pageHeight = doc.internal.pageSize.height;
  
  // Draw the styled header background
  drawPageHeader(doc, title, subtitle, [], { pdfType });
  
  return pageHeight;
};

/**
 * Draw an Executive Summary slide with key metrics
 * @param {Object} doc - jsPDF document
 * @param {Object} options - Slide options
 */
export const drawExecSummarySlide = (doc, options = {}) => {
  const {
    title = 'Executive Summary',
    subtitle = '',
    metrics = [],  // Array of { label, value, change } objects
    summary = '',  // Summary paragraph
    pdfType = 'printable',
  } = options;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Draw the styled header background
  drawPageHeader(doc, title, subtitle, [], { pdfType });
  
  const containerTop = subtitle ? 85 : 70;
  const containerMargin = 20;
  let currentY = containerTop + 20;
  
  // Draw metrics cards if provided
  if (metrics && metrics.length > 0) {
    const cardWidth = (pageWidth - (containerMargin * 2) - 30 - ((metrics.length - 1) * 15)) / metrics.length;
    const cardHeight = pdfType === 'slideshow' ? 80 : 60;
    let cardX = containerMargin + 15;
    
    metrics.forEach((metric, idx) => {
      // Card background
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(cardX, currentY, cardWidth, cardHeight, 6, 6, 'F');
      
      // Metric value (large)
      doc.setFont('Roboto', 'bold');
      doc.setFontSize(pdfType === 'slideshow' ? 28 : 22);
      doc.setTextColor(30, 30, 30);
      const valueY = currentY + (cardHeight * 0.45);
      doc.text(String(metric.value || '—'), cardX + cardWidth / 2, valueY, { align: 'center' });
      
      // Metric label (small)
      doc.setFont('Roboto', 'normal');
      doc.setFontSize(pdfType === 'slideshow' ? 11 : 9);
      doc.setTextColor(100, 100, 100);
      const labelY = currentY + (cardHeight * 0.75);
      doc.text(metric.label || '', cardX + cardWidth / 2, labelY, { align: 'center' });
      
      // Change indicator if provided
      if (metric.change) {
        const isPositive = metric.change > 0;
        doc.setFontSize(pdfType === 'slideshow' ? 10 : 8);
        doc.setTextColor(isPositive ? 34 : 220, isPositive ? 197 : 53, isPositive ? 94 : 69);
        const changeText = `${isPositive ? '↑' : '↓'} ${Math.abs(metric.change)}%`;
        doc.text(changeText, cardX + cardWidth / 2, currentY + (cardHeight * 0.9), { align: 'center' });
      }
      
      cardX += cardWidth + 15;
    });
    
    currentY += cardHeight + 25;
  }
  
  // Draw summary paragraph if provided
  if (summary) {
    const fontSize = pdfType === 'slideshow' ? 12 : 10;
    const lineHeight = fontSize * 1.6;
    const contentWidth = pageWidth - (containerMargin * 2) - 30;
    
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(60, 60, 60);
    
    const wrappedLines = doc.splitTextToSize(summary, contentWidth);
    wrappedLines.forEach(line => {
      if (currentY > pageHeight - 40) return;  // Don't overflow
      doc.text(line, containerMargin + 15, currentY);
      currentY += lineHeight;
    });
  }
  
  return pageHeight;
};

/**
 * Draw a component-based custom page
 * @param {Object} doc - jsPDF document
 * @param {Object} pageConfig - Page configuration with components array
 * @param {string} pdfType - 'printable' or 'slideshow'
 */
export const drawCustomPage = (doc, pageConfig, pdfType = 'printable') => {
  const {
    title = '',
    subtitle = '',
    components = [],
  } = pageConfig;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Draw the styled header background
  drawPageHeader(doc, title, subtitle, [], { pdfType });
  
  // Content area
  const hasSubtitle = subtitle && subtitle.trim().length > 0;
  const containerTop = hasSubtitle ? 85 : 70;
  const containerMargin = 20;
  const contentX = containerMargin + 15;
  const contentWidth = pageWidth - (containerMargin * 2) - 30;
  let currentY = containerTop + 15;

  // Render each component
  components.forEach(comp => {
    if (currentY > pageHeight - 50) return; // Don't overflow

    switch (comp.type) {
      case 'text': {
        const settings = comp.settings || {};
        const fontSize = settings.fontSize || 12;
        const bold = settings.bold || false;
        const align = settings.align || 'left';
        const content = comp.content || '';
        
        const fontStyle = bold ? 'bold' : 'normal';
        doc.setFont('Roboto', fontStyle);
        doc.setFontSize(fontSize);
        doc.setTextColor(50, 50, 50); // Always black
        
        const lineHeight = fontSize * 1.5;
        const bulletIndent = 18;
        
        const lines = content.split('\n');
        lines.forEach(line => {
          const trimmedLine = line.trim();
          if (!trimmedLine) {
            currentY += lineHeight * 0.5;
            return;
          }
          
          // Check if line starts with a bullet marker
          const isBullet = trimmedLine.startsWith('•') || 
                           trimmedLine.startsWith('-') || 
                           trimmedLine.startsWith('*');
          
          let textX = contentX;
          let text = trimmedLine;
          
          if (isBullet) {
            // Remove the marker from text
            text = trimmedLine.substring(1).trim();
            textX = contentX + bulletIndent;
            
            // Draw filled circle bullet
            doc.setFillColor(50, 50, 50);
            const bulletX = contentX + 6;
            const bulletY = currentY - fontSize * 0.3;
            doc.circle(bulletX, bulletY, 2, 'F');
          }
          
          const maxWidth = contentWidth - (isBullet ? bulletIndent : 0);
          const wrappedLines = doc.splitTextToSize(text, maxWidth);
          
          wrappedLines.forEach((wrappedLine, wrapIdx) => {
            if (currentY > pageHeight - 40) return;
            
            // Calculate x position based on alignment
            let drawX = textX;
            // Only apply alignment to non-bullet lines
            if (align === 'center' && !isBullet) {
              const textWidth = doc.getTextWidth(wrappedLine);
              drawX = contentX + (contentWidth - textWidth) / 2;
            } else if (align === 'right' && !isBullet) {
              const textWidth = doc.getTextWidth(wrappedLine);
              drawX = contentX + contentWidth - textWidth;
            } else if (isBullet && wrapIdx > 0) {
              // Continuation lines of bullets stay indented
              drawX = textX;
            }
            
            doc.text(wrappedLine, drawX, currentY);
            currentY += lineHeight;
          });
        });
        
        currentY += 10; // Space after text block
        break;
      }
      
      case 'image': {
        if (!comp.content) break;
        
        try {
          // Image is always centered, full width but never larger than original
          const imageData = comp.content;
          
          // Get image dimensions from the data URL using jsPDF's built-in method
          // We'll add the image and let jsPDF handle aspect ratio
          // First, we need to decode the image to get its natural dimensions
          
          // For base64 images, jsPDF can auto-detect format
          const format = imageData.includes('image/png') ? 'PNG' : 
                         imageData.includes('image/jpeg') ? 'JPEG' : 
                         imageData.includes('image/gif') ? 'GIF' : 'PNG';
          
          // Add image with auto height calculation
          // Use 0 for height to let jsPDF calculate based on aspect ratio
          const imgWidth = contentWidth;
          
          // jsPDF addImage with width only doesn't work well, so we need to calculate
          // We'll use a reasonable max height and let it scale
          const maxHeight = pageHeight - currentY - 50; // Leave room for footer
          
          // Add the image - jsPDF will maintain aspect ratio if we pass 0 for height
          // But that doesn't work reliably, so we'll estimate based on common ratios
          // A better approach: load image to get dimensions
          const imgProps = doc.getImageProperties(imageData);
          const aspectRatio = imgProps.height / imgProps.width;
          
          // Calculate dimensions - use content width but cap at original size
          let finalWidth = Math.min(imgWidth, imgProps.width * 0.75); // 0.75 to convert pixels to points roughly
          let finalHeight = finalWidth * aspectRatio;
          
          // If too tall, scale down
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = finalHeight / aspectRatio;
          }
          
          // Center the image
          const imgX = contentX + (contentWidth - finalWidth) / 2;
          
          doc.addImage(imageData, format, imgX, currentY, finalWidth, finalHeight);
          currentY += finalHeight + 15;
        } catch (e) {
          console.error('Failed to add image:', e);
        }
        break;
      }
      
      case 'metrics': {
        const metrics = Array.isArray(comp.content) ? comp.content : [];
        if (metrics.length === 0) break;
        
        const cardWidth = Math.min(120, (contentWidth - ((metrics.length - 1) * 10)) / metrics.length);
        const cardHeight = pdfType === 'slideshow' ? 70 : 55;
        const totalWidth = (cardWidth * metrics.length) + ((metrics.length - 1) * 10);
        let cardX = contentX + (contentWidth - totalWidth) / 2;
        
        metrics.forEach((metric) => {
          // Card background
          doc.setFillColor(245, 247, 250);
          doc.roundedRect(cardX, currentY, cardWidth, cardHeight, 4, 4, 'F');
          
          // Value
          doc.setFont('Roboto', 'bold');
          doc.setFontSize(pdfType === 'slideshow' ? 20 : 16);
          doc.setTextColor(30, 30, 30);
          doc.text(String(metric.value || '—'), cardX + cardWidth / 2, currentY + cardHeight * 0.45, { align: 'center' });
          
          // Label
          doc.setFont('Roboto', 'normal');
          doc.setFontSize(pdfType === 'slideshow' ? 9 : 8);
          doc.setTextColor(100, 100, 100);
          doc.text(metric.label || '', cardX + cardWidth / 2, currentY + cardHeight * 0.75, { align: 'center' });
          
          cardX += cardWidth + 10;
        });
        
        currentY += cardHeight + 20;
        break;
      }
      
      case 'spacer': {
        const settings = comp.settings || {};
        let spacerHeight;
        
        if (settings.heightType === 'percent') {
          // Calculate height as percentage of usable page height
          const usableHeight = pageHeight - containerTop - 40; // minus header and footer
          spacerHeight = (usableHeight * (settings.heightPercent || 10)) / 100;
        } else {
          spacerHeight = settings.height || 20;
        }
        
        currentY += spacerHeight;
        break;
      }
      
      default:
        break;
    }
  });
  
  return pageHeight;
};

export default {
  drawProjectedPatientsChart,
  drawSharedAffiliationChart,
  drawSharedAffiliationByServiceLineChart,
  drawSharedPatientsChart,
  drawSharedPatientsServiceLineChart,
  drawPayorChart,
  drawChartLegend,
  drawLocationTypeLegend,
  drawAffiliationLegend,
  drawPayorLegend,
  drawAffiliationSummary,
  drawPageHeader,
  drawPageFooterWithLegend,
  drawTitleAndImageSlide,
  drawTitleAndParagraphSlide,
  drawBlankSlide,
  drawExecSummarySlide,
  drawCustomPage,
  AFFILIATION_COLORS,
  DEFAULT_PAYOR_COLORS,
};